import { UIMetadataComplete } from '../../../types/UIMetadata';
import { metadata as _graphics } from './graphics-metadata';

// ── Interior sub-option UI (steering wheel / seat / seat belt / stitch) ──────
// Labels for the accessories generated in graphics-metadata, plus the
// `interiorConfig` map the app uses to drive the interior cascade:
//   interior color → steering wheel + seats (GT2 default / Sport) → per-seat
//   seat-belt & stitch sets (with defaults). Consumed only by linkId 2387;
//   other linkIds have no interiorConfig so the cascade UI is skipped.
// Arabic for the accessory group/category chips. ('BaseState' is an internal
// marker category and is never surfaced in the UI, so it stays as-is.)
const _categoryAr: Record<string, string> = {
  Accessories: 'الإكسسوارات',
  'Alloy Wheels': 'عجلات ألومنيوم',
  Caliper: 'المكابح',
  'Engine Cover': 'غطاء المحرك',
  'Exhaust Tip': 'أطراف العادم',
  Logo: 'الشعار',
  Roof: 'السقف',
  Spoiler: 'الجناح الخلفي',
  Graphics: 'الرسومات',
  Packages: 'الباقات',
  Seat: 'المقاعد',
  'Seat Belt': 'أحزمة الأمان',
  Stitch: 'الخياطة',
  'Steering Wheel': 'عجلة القيادة',
};
const _uiAcc = (en: string, category: string, ar = '') => ({
  label: { en, ar: ar || en },
  image: '',
  category: { en: category, ar: _categoryAr[category] ?? category },
  price: { default: 0 },
  cardType: 'cart' as const,
});

// 3rd tuple element = definite-form Arabic colour used by the belt/stitch label
// templates below ("أحزمة الأمان باللون <X>" / "خياطة باللون <X>").
const _beltColors: Array<[string, string, string]> = [
  ['black', 'Black', 'الأسود'],
  ['orange', 'Orange', 'البرتقالي'],
  ['natural', 'Natural', 'البني'],
  ['blue', 'Blue', 'الأزرق'],
  ['red', 'Red', 'الأحمر'],
  ['yellow', 'Yellow', 'الأصفر'],
];
const _stitchColors: Array<[string, string, string]> = [
  ['white', 'White', 'الأبيض'],
  ['blue', 'Blue', 'الأزرق'],
  ['red', 'Red', 'الأحمر'],
  ['yellow', 'Yellow', 'الأصفر'],
];

const _seatColorNames: Record<string, string> = {
  intJetBlack: 'Jet Black',
  intSkyCoolGray: 'Sky Cool Gray',
  intNatural: 'Natural',
  intAdrenalineRed: 'Adrenaline Red',
  intSantoriniBlue: 'Santorini Blue',
  intNaturalDipped: 'Natural Dipped',
  intAdrenalineRedDipped: 'Adrenaline Red Dipped',
  intAsymmetricalAdrRed: 'Asymmetrical Adrenaline Red / Jet Black',
  intAsymSantoriniJetBlack: 'Asymmetrical Santorini Blue / Jet Black',
  intHabanero: 'Habanero',
  intVeryDarkAtmosphere: 'Very Dark Atmosphere',
  intUltimateSuedeJetBlack: 'Ultimate Suede Jet Black',
};

// Arabic counterparts of _seatColorNames — used by the seat/interior label
// templates so Arabic labels never fall back to the English colour name.
const _seatColorNamesAr: Record<string, string> = {
  intJetBlack: 'أسود',
  intSkyCoolGray: 'رمادي',
  intNatural: 'بني',
  intAdrenalineRed: 'أحمر',
  intSantoriniBlue: 'أزرق',
  intNaturalDipped: 'بني داكن',
  intAdrenalineRedDipped: 'أحمر داكن',
  intAsymmetricalAdrRed: 'أحمر / أسود',
  intAsymSantoriniJetBlack: 'أزرق / أسود',
  intHabanero: 'برتقالي',
  intVeryDarkAtmosphere: 'رمادي داكن جداً',
  intUltimateSuedeJetBlack: 'جلد أسود ناعم فاخر',
};

const _interiorSubUI: Record<string, ReturnType<typeof _uiAcc>> = {
  steering_default: _uiAcc('Standard Steering Wheel', 'Steering Wheel', 'عجلة قيادة قياسية'),
  steering_sueded: _uiAcc('Sueded Microfiber Steering Wheel', 'Steering Wheel', 'عجلة قيادة من الألياف الدقيقة'),
};
_beltColors.forEach(([key, en, ar]) => {
  _interiorSubUI[`seatbelt_gt2_${key}`] = _uiAcc(`${en} Seat Belt`, 'Seat Belt', `أحزمة الأمان باللون ${ar}`);
  _interiorSubUI[`seatbelt_sport_${key}`] = _uiAcc(`${en} Seat Belt`, 'Seat Belt', `أحزمة الأمان باللون ${ar}`);
});
_stitchColors.forEach(([key, en, ar]) => {
  _interiorSubUI[`stitch_gt2_${key}`] = _uiAcc(`${en} Stitching`, 'Stitch', `خياطة باللون ${ar}`);
  _interiorSubUI[`stitch_sport_${key}`] = _uiAcc(`${en} Stitching`, 'Stitch', `خياطة باللون ${ar}`);
});
Object.entries(_seatColorNames).forEach(([colorKey, name]) => {
  const arName = _seatColorNamesAr[colorKey] ?? name;
  _interiorSubUI[`seat_${colorKey}_gt2`] = _uiAcc(`${name} — GT2 Seats`, 'Seat', `${arName} — مقاعد GT2`);
  _interiorSubUI[`seat_${colorKey}_sport`] = _uiAcc(`${name} — Competition Sport Seats`, 'Seat', `${arName} — مقاعد المنافسة الرياضية`);
});
// Asymmetrical Santorini Blue / Jet Black has an extra third seat (Sport GT2).
_interiorSubUI['seat_intAsymSantoriniJetBlack_sport_gt2'] = _uiAcc('Asymmetrical Santorini Blue / Jet Black — Competition Sport GT2 Seats', 'Seat', 'أزرق / أسود — مقاعد المنافسة الرياضية GT2');
// Sueded-microfiber seat variants (8 colors) — cards inside the sueded color panels.
['intJetBlack', 'intSkyCoolGray', 'intNatural', 'intAdrenalineRed', 'intNaturalDipped', 'intHabanero', 'intVeryDarkAtmosphere', 'intUltimateSuedeJetBlack'].forEach((colorKey) => {
  const name = _seatColorNames[colorKey];
  const arName = _seatColorNamesAr[colorKey] ?? name;
  _interiorSubUI[`seat_${colorKey}_gt2_sueded`] = _uiAcc(`${name} — GT2 Seats`, 'Seat', `${arName} — مقاعد GT2`);
  _interiorSubUI[`seat_${colorKey}_sport_sueded`] = _uiAcc(`${name} — Competition Sport Seats`, 'Seat', `${arName} — مقاعد المنافسة الرياضية`);
});
// Graphics / stripes UI labels (config-id graphics_<sku> matches graphics-metadata)
// Arabic for the graphics/stripe labels, keyed by the English name. The Stingray,
// GS X and convertible SKU maps reuse the same English names, so one map covers
// all three generators below.
const _graphicsAr: Record<string, string> = {
  'Asymmetrical Edge Red/Carbon Flash Full Length Dual Racing Stripes':
    'خطوط سباق مزدوجة غير متماثلة بطول الهيكل باللونين الأحمر والأسود المعدني',
  'Asymmetrical Royal Blue/Carbon Flash Full Length Dual Racing Stripes':
    'خطوط سباق مزدوجة غير متماثلة بطول الهيكل باللونين الأزرق الملكي والأسود المعدني',
  'Carbon Flash with Blue accent Full Length Dual Racing Stripes':
    'خطوط سباق مزدوجة بطول الهيكل باللون الأسود المعدني مع لمسات باللون الأزرق',
  'Carbon Flash with Yellow accent Full Length Dual Racing Stripes':
    'خطوط سباق مزدوجة بطول الهيكل باللون الأسود المعدني مع لمسات باللون الأصفر',
  'Carbon Flash with Orange accent Full Length Dual Racing Stripes':
    'خطوط سباق مزدوجة بطول الهيكل باللون الأسود المعدني مع لمسات باللون البرتقالي',
  'Carbon Flash with Red accent Full Length Dual Racing Stripes':
    'خطوط سباق مزدوجة بطول الهيكل باللون الأسود المعدني مع لمسات باللون الأحمر',
  'Carbon Flash with Silver accent Full Length Dual Racing Stripes':
    'خطوط سباق مزدوجة بطول الهيكل باللون الأسود المعدني مع لمسات باللون الفضي',
  'Carbon Flash Metallic Full Length Dual Racing Stripes':
    'خطوط سباق مزدوجة بطول الهيكل باللون الأسود المعدني',
  'Edge Orange Full Length Dual Racing Stripes': 'خطوط سباق مزدوجة بطول الهيكل باللون البرتقالي',
  'Edge Red Full Length Dual Racing Stripes': 'خطوط سباق مزدوجة بطول الهيكل باللون الأحمر',
  'Sterling Silver Full Length Dual Racing Stripes': 'خطوط سباق مزدوجة بطول الهيكل باللون الفضي',
  'Competition Yellow Full Length Dual Racing Stripes': 'خطوط سباق مزدوجة بطول الهيكل باللون الأصفر',
  'Carbon Flash/Edge Red Stinger Stripe': 'خط ستينغر باللونين الأسود المعدني والأحمر',
  'Carbon Flash/Competition Yellow Stinger Stripe': 'خط ستينغر باللونين الأسود المعدني والأصفر',
  'Carbon Flash/Midnight Silver Stinger Stripe': 'خط ستينغر باللونين الأسود المعدني والفضي الداكن',
  'Stinger — Electric Blue': 'خط ستينغر باللون الأزرق الكهربائي',
  'Edge Blue': 'خطوط باللون الأزرق',
  'Admiral Blue Grand Sport Heritage Hash Marks': 'شارات جراند سبورت التراثية باللون الأزرق',
  'Carbon Flash Grand Sport Heritage Hash Marks': 'شارات جراند سبورت التراثية باللون الأسود المعدني',
  'Red Mist Grand Sport Heritage Hash Marks': 'شارات جراند سبورت التراثية باللون الأحمر الضبابي',
  'Torch Red Grand Sport Heritage Hash Marks': 'شارات جراند سبورت التراثية باللون الأحمر',
  'Blade Silver Grand Sport Heritage Hash Marks': 'شارات جراند سبورت التراثية باللون الفضي',
  'Competition Yellow Grand Sport Heritage Hash Marks': 'شارات جراند سبورت التراثية باللون الأصفر',
};
const _graphicsNames: Record<string, string> = {
  '6821': 'Asymmetrical Edge Red/Carbon Flash Full Length Dual Racing Stripes',
  '6857': 'Asymmetrical Royal Blue/Carbon Flash Full Length Dual Racing Stripes',
  '6843': 'Carbon Flash with Blue accent Full Length Dual Racing Stripes',
  '6842': 'Carbon Flash with Yellow accent Full Length Dual Racing Stripes',
  '6847': 'Carbon Flash with Orange accent Full Length Dual Racing Stripes',
  '6832': 'Carbon Flash with Red accent Full Length Dual Racing Stripes',
  '6838': 'Carbon Flash with Silver accent Full Length Dual Racing Stripes',
  '6824': 'Carbon Flash Metallic Full Length Dual Racing Stripes',
  '6839': 'Edge Orange Full Length Dual Racing Stripes',
  '6835': 'Edge Red Full Length Dual Racing Stripes',
  '6845': 'Sterling Silver Full Length Dual Racing Stripes',
  '6827': 'Competition Yellow Full Length Dual Racing Stripes',
  '6823': 'Carbon Flash/Edge Red Stinger Stripe',
  '6831': 'Carbon Flash/Competition Yellow Stinger Stripe',
  '6829': 'Carbon Flash/Midnight Silver Stinger Stripe',
};
Object.entries(_graphicsNames).forEach(([sku, name]) => {
  _interiorSubUI[`graphics_${sku}`] = _uiAcc(name, 'Graphics', _graphicsAr[name] ?? name);
});

// ── GS X accessory UI labels (keys match graphics-metadata _gsAccessories) ───
Object.assign(_interiorSubUI, {
  gsCaliperDarkGray: _uiAcc('Dark Gray Metallic Calipers', 'Caliper', 'مكابح باللون الرمادي المعدني'),
  gsCaliperBrightRed: _uiAcc('Bright Red Calipers', 'Caliper', 'مكابح مطلية باللون الأحمر الفاتح'),
  gsCaliperEdgeRed: _uiAcc('Edge Red Calipers', 'Caliper', 'مكابح باللون الأحمر'),
  gsCaliperVelocityYellow: _uiAcc('Velocity Yellow Calipers', 'Caliper', 'مكابح باللون الأصفر'),
  gsCaliperOrange: _uiAcc('Orange Calipers', 'Caliper', 'مكابح مطلية باللون البرتقالي'),
  gsAlloyROU: _uiAcc('Pearl Nickel forged aluminum', 'Alloy Wheels', 'عجلات ألومنيوم مطروقة باللون النيكل اللؤلؤي'),
  gsAlloyROX: _uiAcc('Carbon Flash with machined edge forged aluminum', 'Alloy Wheels', 'عجلات ألومنيوم مطروقة باللون الأسود المعدني مع حافة مصقولة'),
  gsAlloySOM: _uiAcc('bright polished forged aluminum', 'Alloy Wheels', 'عجلات ألومنيوم مطروقة مصقولة لامعة'),
  gsAlloySON: _uiAcc('Gloss Black forged aluminum', 'Alloy Wheels', 'عجلات ألومنيوم مطروقة باللون الأسود اللامع'),
  gsAlloyROY: _uiAcc('Carbon Flash-painted carbon fiber', 'Alloy Wheels', 'ألياف كربون مطلية باللون الأسود المعدني'),
  gsAlloyROZ: _uiAcc('With visible carbon fiber', 'Alloy Wheels', 'مع ألياف الكربون المرئية'),
  gsAlloySTZ: _uiAcc('With visible carbon fiber with Red stripe', 'Alloy Wheels', 'مع ألياف الكربون المرئية وخط أحمر'),
  gsCarbonFlashDefault: _uiAcc('Body-color Mirrors & Spoiler', 'BaseState', 'مرايا خارجية وجناح خلفي بلون الهيكل'),
  gsCarbonFlashPkg: _uiAcc('Carbon Flash Metallic-painted Outside Mirrors and Spoiler', 'Packages', 'مرايا خارجية وجناح خلفي مطليان باللون الأسود المعدني'),
  gsGroundEffectsNone: _uiAcc('Standard Ground Effects', 'BaseState', 'تأثيرات أرضية قياسية'),
  gsGroundEffectsCarbonFiber: _uiAcc('Visible Carbon Fiber Ground Effects', 'Packages', 'تأثيرات أرضية من ألياف الكربون'),
  gsGroundEffectsCarbonFlash: _uiAcc('Carbon Flash Ground Effects', 'Packages', 'تأثيرات أرضية باللون الأسود المعدني'),
  gsBadgeBlackCoupe: _uiAcc('Black Logo', 'Logo', 'شعار أسود'),
  gsBadgeChromeCoupe: _uiAcc('Chrome Exterior Badge Package', 'Packages', 'باقة الشعارات الخارجية من الكروم'),
  gsBadgeBlackConv: _uiAcc('Black Logo', 'Logo', 'شعار أسود'),
  gsBadgeChromeConv: _uiAcc('Chrome Exterior Badge Package', 'Packages', 'باقة الشعارات الخارجية من الكروم'),
  gsBodyColorAccent: _uiAcc('Body-color Exterior Accents', 'Packages', 'لمسات خارجية بلون الهيكل'),
});
const _gsGraphicsNames: Record<string, string> = {
  '6698': 'Asymmetrical Edge Red/Carbon Flash Full Length Dual Racing Stripes',
  '7003': 'Asymmetrical Royal Blue/Carbon Flash Full Length Dual Racing Stripes',
  '6795': 'Carbon Flash with Blue accent Full Length Dual Racing Stripes',
  '6707': 'Carbon Flash with Yellow accent Full Length Dual Racing Stripes',
  '6785': 'Carbon Flash with Orange accent Full Length Dual Racing Stripes',
  '6717': 'Carbon Flash with Red accent Full Length Dual Racing Stripes',
  '6695': 'Carbon Flash with Silver accent Full Length Dual Racing Stripes',
  '6703': 'Carbon Flash Metallic Full Length Dual Racing Stripes',
  '6721': 'Edge Orange Full Length Dual Racing Stripes',
  '6787': 'Edge Red Full Length Dual Racing Stripes',
  '6786': 'Sterling Silver Full Length Dual Racing Stripes',
  '6723': 'Competition Yellow Full Length Dual Racing Stripes',
  '6714': 'Carbon Flash/Edge Red Stinger Stripe',
  '6747': 'Carbon Flash/Competition Yellow Stinger Stripe',
  '6741': 'Carbon Flash/Midnight Silver Stinger Stripe',
  // Grand Sport Heritage Hash Marks (coupe)
  '7013': 'Admiral Blue Grand Sport Heritage Hash Marks',
  '7009': 'Carbon Flash Grand Sport Heritage Hash Marks',
  '7002': 'Red Mist Grand Sport Heritage Hash Marks',
  '7011': 'Torch Red Grand Sport Heritage Hash Marks',
  '6996': 'Blade Silver Grand Sport Heritage Hash Marks',
  '6991': 'Competition Yellow Grand Sport Heritage Hash Marks',
};
Object.entries(_gsGraphicsNames).forEach(([sku, name]) => {
  _interiorSubUI[`gsGraphics_${sku}`] = _uiAcc(name, 'Graphics', _graphicsAr[name] ?? name);
});

// belt default per color/seat (black for most; natural for Natural Dipped; orange for Habanero)
const _beltDefault = (colorKey: string, seat: 'gt2' | 'sport') => {
  if (colorKey === 'intNaturalDipped') return `seatbelt_${seat}_natural`;
  if (colorKey === 'intHabanero') return `seatbelt_${seat}_orange`;
  return `seatbelt_${seat}_black`;
};
const _beltOptions = (seat: 'gt2' | 'sport') => _beltColors.map(([k]) => `seatbelt_${seat}_${k}`);
const _stitchOptions = (seat: 'gt2' | 'sport') => _stitchColors.map(([k]) => `stitch_${seat}_${k}`);

// ── Interior colour RPO codes ────────────────────────────────────────────────
// Source: "2026 Chevrolet Car Corvette — COLOR AND TRIM (Middle East)", published
// 2025-06-27. The RPO for an interior colour is NOT fixed per colour: it varies by
//   • MODEL      — 1LT / 2LT / (3LT & 3LZ share codes), and
//   • SEAT TRIM  — Mulan leather (GT1) / Performance Textile / Napa perforated /
//                  Napa perforated + sueded microfiber.
// Seat TYPE does not change it: the sheet puts GT2 and Competition buckets
// (AH2 / AE4) on one shared row, and the mixed AUP row repeats the same HVZ.
//
// Our colour ids already encode the trim (int1lt*Mulan / int1lt*Performance /
// base = Napa perforated / *Sueded = + sueded microfiber). The *Conv ids are used by
// both convertibles — conv_2lt is now "Convertible 3LT", so it shares the SAME codes
// as gsx_conv_3lz (_INT_RPO_3L_CONV). Lookup is still per-variant because coupe and
// convertible use different colour ids.
const _INT_RPO_1LT: Record<string, string> = {
  // GT1 buckets (AQ9) — Mulan leather, perforated inserts
  int1ltJetBlackMulan: 'HTA',
  int1ltSkyCoolGrayMulan: 'HUP',
  int1ltAdrenalineRedMulan: 'HUQ',
  // Competition buckets (AE4) — Performance Textile
  int1ltJetBlackPerformance: 'HTJ',
};
// Model 2LT — Mulan leather with perforated inserts. Taken from the AOL price
// schedule: 1YC07/&2LT lists exactly four interior codes, all `&MAM&2LT/2LZ` at
// $149.60, and their descriptions match these four colours one-for-one.
const _INT_RPO_2LT: Record<string, string> = {
  int2ltJetBlackMulan: 'HTP',
  int2ltSkyCoolGrayMulan: 'HU6',
  int2ltAdrenalineRedMulan: 'HU7',
  int2ltNaturalMulan: 'HTQ',
};
// Models 3LT & 3LZ — GT2 / Competition buckets (AH2 / AE4), Napa leather. Coupe ids.
const _INT_RPO_3L: Record<string, string> = {
  intJetBlack: 'HTE',
  intSkyCoolGray: 'HU1',
  intAdrenalineRed: 'HU2',
  intAdrenalineRedDipped: 'HNK',
  intNatural: 'HUE',
  intNaturalDipped: 'HZN',
  intSantoriniBlue: 'H8T',
  intHabanero: 'HUW',
  intVeryDarkAtmosphere: 'EJH',
  intUltimateSuedeJetBlack: 'HUC',
  intAsymmetricalAdrRed: 'HVZ',
  // + sueded microfiber inserts (only these 8 colours offer it)
  intJetBlackSueded: 'HTT',
  intSkyCoolGraySueded: 'HU9',
  intAdrenalineRedSueded: 'HUA',
  intNaturalSueded: 'HTG',
  intNaturalDippedSueded: 'HUF',
  intHabaneroSueded: 'HUX',
  intVeryDarkAtmosphereSueded: 'EPX',
  intUltimateSuedeJetBlackSueded: 'HUB',
};
// GS X convertible is model 3LZ, so it uses the 3LT/3LZ codes — but on the *Conv ids.
const _INT_RPO_3L_CONV: Record<string, string> = Object.fromEntries(
  Object.entries(_INT_RPO_3L).map(([id, code]) => [`${id}Conv`, code]),
);
const _interiorColorRPO: Record<string, Record<string, string>> = {
  coupe_1lt: _INT_RPO_1LT,
  coupe_2lt: _INT_RPO_2LT,
  conv_2lt: _INT_RPO_3L_CONV, // "Convertible 3LT" — same interior RPO codes as gsx_conv_3lz
  coupe_3lt: _INT_RPO_3L,
  gsx_coupe_3lz: _INT_RPO_3L,
  gsx_conv_3lz: _INT_RPO_3L_CONV,
};

// Per the Corvette Table, contrast stitch is offered ONLY on the Jet Black
// interior; every other Napa color has just steering + seat + seat belt.
const _COLORS_WITH_STITCH = ['intJetBlack'];
// These interiors have no seat-belt-color choice, so the belt accordion is hidden.
const _COLORS_WITHOUT_BELT = ['intSantoriniBlue', 'intAsymmetricalAdrRed', 'intAdrenalineRedDipped', 'intAsymSantoriniJetBlack'];
const _interiorConfig = Object.keys(_seatColorNames).reduce((cfg, colorKey) => {
  const mkSeat = (seat: 'gt2' | 'sport') => {
    const s: any = {};
    if (!_COLORS_WITHOUT_BELT.includes(colorKey)) {
      s.seatBelt = { default: _beltDefault(colorKey, seat), options: _beltOptions(seat) };
    }
    if (_COLORS_WITH_STITCH.includes(colorKey)) {
      s.stitch = { default: `stitch_${seat}_white`, options: _stitchOptions(seat) };
    }
    return s;
  };
  const entry = {
    steeringWheelId: 'steering_default',
    defaultSeatId: `seat_${colorKey}_gt2`,
    seats: {
      [`seat_${colorKey}_gt2`]: { label: { en: 'GT2 Seats', ar: 'مقاعد GT2' }, ...mkSeat('gt2') },
      [`seat_${colorKey}_sport`]: { label: { en: 'Competition Sport Seats', ar: 'مقاعد المنافسة الرياضية' }, ...mkSeat('sport') },
    },
  };
  cfg[colorKey] = entry;
  // Convertible interior colors reuse the shared seats → same cascade config.
  cfg[`${colorKey}Conv`] = entry;
  return cfg;
}, {} as Record<string, any>);
// Asymmetrical Santorini Blue / Jet Black offers a THIRD seat (Sport GT2) on top of the
// standard GT2 (default) + Sport pair. Asymmetrical colours have no belt/stitch options.
_interiorConfig['intAsymSantoriniJetBlack'].seats['seat_intAsymSantoriniJetBlack_sport_gt2'] = {
  label: { en: 'Competition Sport GT2 Seats', ar: 'مقاعد المنافسة الرياضية GT2' },
};

// Sueded-microfiber interior-color cards: same cascade but sueded seats +
// sueded-microfiber steering wheel. Only the 8 colors with a sueded seat variant.
const _SUEDED_COLORS = ['intJetBlack', 'intSkyCoolGray', 'intNatural', 'intAdrenalineRed', 'intNaturalDipped', 'intHabanero', 'intVeryDarkAtmosphere', 'intUltimateSuedeJetBlack'];
_SUEDED_COLORS.forEach((colorKey) => {
  const mkSeat = (seat: 'gt2' | 'sport') => {
    const s: any = { seatBelt: { default: _beltDefault(colorKey, seat), options: _beltOptions(seat) } };
    if (_COLORS_WITH_STITCH.includes(colorKey)) s.stitch = { default: `stitch_${seat}_white`, options: _stitchOptions(seat) };
    return s;
  };
  const entry = {
    steeringWheelId: 'steering_sueded',
    defaultSeatId: `seat_${colorKey}_gt2_sueded`,
    seats: {
      [`seat_${colorKey}_gt2_sueded`]: { label: { en: 'GT2 Seats', ar: 'مقاعد GT2' }, ...mkSeat('gt2') },
      [`seat_${colorKey}_sport_sueded`]: { label: { en: 'Competition Sport Seats', ar: 'مقاعد المنافسة الرياضية' }, ...mkSeat('sport') },
    },
  };
  _interiorConfig[`${colorKey}Sueded`] = entry;
  _interiorConfig[`${colorKey}SuededConv`] = entry;
});

// Stingray Coupe-1LT: seatless cascade — the interior color loads the seat, so
// there is no GT2/Sport toggle. Only steering wheel + seat belts apply.
// Mulan leather colours use the GT1 leather belt set.
const _GT1_BELTS = ['seatbelt_gt1_black', 'seatbelt_gt1_orange', 'seatbelt_gt1_natural', 'seatbelt_gt1_blue', 'seatbelt_gt1_red', 'seatbelt_gt1_yellow'];
['int1ltJetBlackMulan', 'int1ltSkyCoolGrayMulan', 'int1ltAdrenalineRedMulan'].forEach((k) => {
  _interiorConfig[k] = {
    steeringWheelId: 'steering_default',
    seatBelt: { default: 'seatbelt_gt1_black', options: _GT1_BELTS },
  };
});
// Jet Black Performance textile uses a DIFFERENT belt set than the Mulan
// leather colours — the Competition Sport belts (same setup as the 3LT Jet
// Black Napa Sport seat). Applying the GT1 leather belt here left a second,
// overlapping belt; the Sport set is the textile interior's own belt.
_interiorConfig['int1ltJetBlackPerformance'] = {
  steeringWheelId: 'steering_default',
  seatBelt: { default: 'seatbelt_sport_black', options: _beltOptions('sport') },
};
// Stingray Coupe-2LT: Mulan leather + GT1 belts, seatless cascade (same shape as 1LT).
['int2ltJetBlackMulan', 'int2ltSkyCoolGrayMulan', 'int2ltAdrenalineRedMulan', 'int2ltNaturalMulan'].forEach((k) => {
  _interiorConfig[k] = {
    steeringWheelId: 'steering_default',
    seatBelt: { default: 'seatbelt_gt1_black', options: _GT1_BELTS },
  };
});

// Convertible interior-color card images (Convertible Napa color codes from the sheet).
const _convIntImg: Record<string, string> = {
  intJetBlack: 'JetBlack_Napa_Leather_Convertible',
  intSkyCoolGray: 'Sky_Cool_Grey_Napa_Leather_Convertible',
  intNatural: 'Natural_Napa_Leather_Convertible',
  intAdrenalineRed: 'Adrenaline_Red_Napa_Leather_Convertible',
  intSantoriniBlue: 'Santorini_Blue_Napa_Leather_Convertible',
  intNaturalDipped: 'Natural_Dipped_Napa_Leather_Convertible',
  intAdrenalineRedDipped: 'Adrenaline_Red_Dipped_Napa_Leather_Convertible',
  intAsymmetricalAdrRed: 'Asymmetrical_Adrenaline_Red_Napa_Leather_Convertible',
  intHabanero: 'Habenera_Napa_Leather_Convertible',
  intVeryDarkAtmosphere: 'Very_Dark_Atmosphere_Napa_Leather_Convertible',
  intUltimateSuedeJetBlack: 'Ultimate_Suede_JetBlack_Napa_Leather_Convertible',
};
const _convInteriorColorUI = Object.keys(_seatColorNames).reduce((acc, k, i) => {
  const name = _seatColorNames[k];
  const arName = _seatColorNamesAr[k] ?? name;
  acc[`${k}Conv`] = {
    label: { en: `${name}, Napa leather with perforated inserts`, ar: `${arName}، جلد نابا مع لمسات مثقبة` },
    image: `images/2387/colors/int/${_convIntImg[k]}.jpg`,
    usdz: '',
    price: { default: 0 },
    index: i,
    type: 'interior',
    cardType: 'cart',
    cartImage: '',
    hasQuantity: false,
  };
  return acc;
}, {} as Record<string, any>);
// Sueded-microfiber interior-color cards (coupe + convertible) for the 8 colors.
const _suededInteriorColorUI = ['intJetBlack', 'intSkyCoolGray', 'intNatural', 'intAdrenalineRed', 'intNaturalDipped', 'intHabanero', 'intVeryDarkAtmosphere', 'intUltimateSuedeJetBlack'].reduce((acc, k, i) => {
  const name = _seatColorNames[k];
  const arName = _seatColorNamesAr[k] ?? name;
  const mk = () => ({ label: { en: `${name}, Napa leather with sueded microfiber inserts`, ar: `${arName}، جلد نابا مع لمسات مثقبة من جلد الألياف الدقيقة` }, image: '', usdz: '', price: { default: 0 }, index: 100 + i, type: 'interior', cardType: 'cart', cartImage: '', hasQuantity: false });
  acc[`${k}Sueded`] = mk();
  acc[`${k}SuededConv`] = mk();
  return acc;
}, {} as Record<string, any>);

// Convertible accessory UI labels (keys match graphics-metadata _convAccessories).
Object.assign(_interiorSubUI, {
  convRoofStandard: _uiAcc('Standard Convertible Roof', 'Roof', 'السقف المكشوف القياسي.'),
  convRoofCarbonFlash: _uiAcc('Carbon Flash Convertible Roof', 'Roof', 'سقف كابريوليه باللون الأسود المعدني'),
  convRoofCarbonFlashBody: _uiAcc('Body-color Convertible Roof', 'Roof', 'سقف كابريوليه بلون الهيكل'),
  convPkgStealthTrim: _uiAcc('Stealth Interior Trim Package', 'Packages', 'باقة Stealth الداخلية'),
  convPkgCarbonFiberTrim: _uiAcc('Carbon Fiber Interior Trim Package', 'Packages', 'باقة تطعيمات داخلية من ألياف الكربون'),
  convPkgEngineAppearance: _uiAcc('Engine Appearance Package', 'Packages', 'باقة مظهر المحرك'),
  convBadgeBlackStingray: _uiAcc('Black Logo', 'Logo', 'شعار أسود'),
  convBadgeChromeStingray: _uiAcc('Chrome Exterior Badge Package', 'Packages', 'باقة الشعارات الخارجية من الكروم'),
  accVariantPartsConv2lt: { label: { en: 'Convertible Stingray 2LT Parts', ar: 'أجزاء ستينغراي المكشوف 2LT' }, image: '', category: { en: 'Accessories', ar: 'الإكسسوارات' }, price: { default: 0 }, cardType: 'cart' },
  accVariantPartsGsxConv: { label: { en: 'GS X Convertible Parts', ar: 'أجزاء جراند سبورت X مكشوف' }, image: '', category: { en: 'Accessories', ar: 'الإكسسوارات' }, price: { default: 0 }, cardType: 'cart' },
});
const _convGraphicsNames: Record<string, string> = {
  '6841': 'Asymmetrical Edge Red/Carbon Flash Full Length Dual Racing Stripes',
  '6867': 'Asymmetrical Royal Blue/Carbon Flash Full Length Dual Racing Stripes',
  '6834': 'Carbon Flash with Blue accent Full Length Dual Racing Stripes',
  '6840': 'Carbon Flash with Yellow accent Full Length Dual Racing Stripes',
  '6833': 'Carbon Flash with Orange accent Full Length Dual Racing Stripes',
  '6844': 'Carbon Flash with Red accent Full Length Dual Racing Stripes',
  '6826': 'Carbon Flash with Silver accent Full Length Dual Racing Stripes',
  '6828': 'Carbon Flash Metallic Full Length Dual Racing Stripes',
  '6848': 'Edge Orange Full Length Dual Racing Stripes',
  '6822': 'Edge Red Full Length Dual Racing Stripes',
  '6836': 'Sterling Silver Full Length Dual Racing Stripes',
  '6825': 'Competition Yellow Full Length Dual Racing Stripes',
  '6823': 'Carbon Flash/Edge Red Stinger Stripe',
  '6831': 'Carbon Flash/Competition Yellow Stinger Stripe',
  '6829': 'Carbon Flash/Midnight Silver Stinger Stripe',
  '6837': 'Stinger — Electric Blue',
  '6812': 'Asymmetrical Edge Red/Carbon Flash Full Length Dual Racing Stripes',
  '6995': 'Asymmetrical Royal Blue/Carbon Flash Full Length Dual Racing Stripes',
  '6808': 'Carbon Flash with Blue accent Full Length Dual Racing Stripes',
  '6805': 'Carbon Flash with Yellow accent Full Length Dual Racing Stripes',
  '6806': 'Carbon Flash with Orange accent Full Length Dual Racing Stripes',
  '6811': 'Carbon Flash with Red accent Full Length Dual Racing Stripes',
  '6816': 'Carbon Flash with Silver accent Full Length Dual Racing Stripes',
  '6807': 'Edge Blue',
  '6818': 'Carbon Flash Metallic Full Length Dual Racing Stripes',
  '6819': 'Edge Orange Full Length Dual Racing Stripes',
  '6809': 'Edge Red Full Length Dual Racing Stripes',
  '6817': 'Sterling Silver Full Length Dual Racing Stripes',
  '6820': 'Competition Yellow Full Length Dual Racing Stripes',
  // Grand Sport Heritage Hash Marks (convertible)
  '7016': 'Admiral Blue Grand Sport Heritage Hash Marks',
  '7019': 'Carbon Flash Grand Sport Heritage Hash Marks',
  '7014': 'Red Mist Grand Sport Heritage Hash Marks',
  '7018': 'Torch Red Grand Sport Heritage Hash Marks',
  '7017': 'Blade Silver Grand Sport Heritage Hash Marks',
  '7015': 'Competition Yellow Grand Sport Heritage Hash Marks',
};
Object.entries(_convGraphicsNames).forEach(([sku, name]) => {
  _interiorSubUI[`convGraphics_${sku}`] = _uiAcc(name, 'Graphics', _graphicsAr[name] ?? name);
});

// Hidden base-state defaults (display:false in graphics; labels for key parity).
Object.assign(_interiorSubUI, {
  accBodyAccentDefaultStingray: _uiAcc('Body-color Exterior Accents', 'BaseState', 'لمسات خارجية بلون الهيكل'),
  accBodyAccentDefaultGs: _uiAcc('Body-color Exterior Accents', 'BaseState', 'لمسات خارجية بلون الهيكل'),
  convEngineAppearanceDefault: _uiAcc('Standard Engine Appearance', 'BaseState', 'مظهر المحرك القياسي.'),
  accZ51None: _uiAcc('Standard Suspension', 'BaseState', 'نظام تعليق قياسي'),
  accOrvmBodyColor: _uiAcc('Body-color Mirrors', 'BaseState', 'مرايا خارجية بلون الهيكل'),
  accOrvmCarbonFlash: _uiAcc('Carbon Flash Metallic Mirrors and Spoiler', 'Packages', 'مرايا خارجية وجناح خلفي باللون الأسود المعدني'),
});

// ── Stingray Coupe-1LT UI labels (GT1 belts, variant parts, transparent roof) ──
Object.assign(_interiorSubUI, {
  seatbelt_gt1_black: _uiAcc('Black Seat Belt', 'Seat Belt', 'أحزمة الأمان باللون الأسود'),
  seatbelt_gt1_orange: _uiAcc('Orange Seat Belt', 'Seat Belt', 'أحزمة الأمان باللون البرتقالي'),
  seatbelt_gt1_natural: _uiAcc('Natural Seat Belt', 'Seat Belt', 'أحزمة الأمان باللون البني'),
  seatbelt_gt1_blue: _uiAcc('Blue Seat Belt', 'Seat Belt', 'أحزمة الأمان باللون الأزرق'),
  seatbelt_gt1_red: _uiAcc('Red Seat Belt', 'Seat Belt', 'أحزمة الأمان باللون الأحمر'),
  seatbelt_gt1_yellow: _uiAcc('Yellow Seat Belt', 'Seat Belt', 'أحزمة الأمان باللون الأصفر'),
  accVariantPartsCoupe1lt: { label: { en: 'Coupe Stingray 1LT Parts', ar: 'أجزاء كوبيه ستينغراي 1LT' }, image: '', category: { en: 'Accessories', ar: 'الإكسسوارات' }, price: { default: 0 }, cardType: 'cart' },
  accVariantPartsCoupe2lt: { label: { en: 'Coupe Stingray 2LT Parts', ar: 'أجزاء كوبيه ستينغراي 2LT' }, image: '', category: { en: 'Accessories', ar: 'الإكسسوارات' }, price: { default: 0 }, cardType: 'cart' },
});

// ── Regions: display currency + USD conversion factor ───────────────────────
// Keyed by the `country` URL parameter (same keys as config.countryCodes below).
// `fx` is local-currency units per 1 USD, so `local = usd * fx`. Source: the FX
// table (cols M/N) of "MY27 Corvette Y2XX_Marketing Communication 05 May 2026".
//
// Variant prices are quoted directly in local currency by that sheet and are
// stored verbatim on each variant. Option/colour/accessory prices come from the
// AOL price schedule in USD only and are converted through `fx` — see
// `_optionPriceUSD` and the pricing pass at the bottom of this file.
//
// Lebanon's FX is ~1.0 because GM quotes Lebanese prices in USD, not LBP — hence
// `currency: 'USD'` against the `LBP` country key. Iraq (IQD) has no GM-published
// pricing either; it is priced in USD at fx 1 (client instruction), so options
// convert from their USD figure and trims from UAE / the UAE fx.
const _regions: Record<string, { label: string; currency: string; fx: number }> = {
  KSA: { label: 'KSA', currency: 'SAR', fx: 3.750794061246906 },
  UAE: { label: 'UAE', currency: 'AED', fx: 3.6809475499749165 },
  OMR: { label: 'Oman', currency: 'OMR', fx: 0.3852985621639979 },
  BHD: { label: 'Bahrain', currency: 'BHD', fx: 0.37756529704382324 },
  QAR: { label: 'Qatar', currency: 'QAR', fx: 3.6408212185782456 },
  KW: { label: 'Kuwait', currency: 'KWD', fx: 0.3069084312374615 },
  LBP: { label: 'Lebanon', currency: 'USD', fx: 1.0002602764627688 },
  IQD: { label: 'Iraq', currency: 'USD', fx: 1 },
  JOD: { label: 'Jordan', currency: 'JOD', fx: 0.710150886612029 },
  default: { label: 'UAE', currency: 'AED', fx: 3.6809475499749165 },
};

// ── Option prices, USD ──────────────────────────────────────────────────────
// Generated from "MY27 Corvette Price Schedule - AOL.xlsx" (GM Overseas
// Distribution, eff. 01/01/2026) by scripts/gen-option-prices.py, keyed by RPO
// code — the same code entries below already carry as `autoIntelRPOCode`, and
// that interior colours resolve through `_interiorColorRPO`.
//
// Extraction rules, applied per (MODEL CODE, PEG) for the five trims we ship:
//   * When a code has several rows, prefer the row WITHOUT `&MAM`.
//   * `&MAM` is a tiebreaker, not an exclusion — a code that exists ONLY as
//     `&MAM` keeps that price. Every paint and interior colour is in this
//     bucket, so dropping them outright would leave all colours unpriced.
//   * Among what remains, take the row whose restriction names this PEG.
//
// Prices are PER TRIM, not global, because the schedule only lists an option on
// the trims where it actually costs extra. Two cases make this load-bearing:
//   * AE4 (Competition Sport Seats) is $1843.60 on 2LT but $523.60 on 3LT/3LZ.
//   * AH2 (GT2 Bucket Seats) is priced on 2LT only. The accessory that carries
//     AH2 is offered on the 3LT trims, where GT2 seats are standard — so a
//     global code→price table would wrongly bill it $1491.60.
// An entry therefore takes a price only where its own `relevantVariants` and
// this table overlap. `'*'` = same price on all five trims.
//
// Codes absent from the schedule are standard/no-cost and keep `price: 0`.
// Worth knowing: ROU ("Wheels 20 Front 21 Rear", $875.60) is priced on the
// Grand Sport and Z06 trims but not on GS X, so `gsAlloyROU` stays at 0.
const _optionPriceUSD: Record<string, Record<string, number>> = {
  '17A': { gsx_coupe_3lz: 875.6, gsx_conv_3lz: 875.6 },                       // Blade Silver Stripe Heritage
  '20A': { gsx_coupe_3lz: 875.6, gsx_conv_3lz: 875.6 },                       // Admiral Blue Heritage
  '36S': { '*': 435.6 },                                                      // Yellow Custom Leather Stitch
  '379': { '*': 523.6 },                                                      // Orange Seat Belt
  '37S': { '*': 435.6 },                                                      // Blue Custom Leather Stitch
  '38S': { '*': 435.6 },                                                      // Adrenaline Red Custom Leather
  '3A9': { '*': 523.6 },                                                      // Santorini Blue Seat Belts
  '3F9': { '*': 523.6 },                                                      // Torch Red Seat Belt
  '3M9': { '*': 523.6 },                                                      // Yellow Seat Belts
  '3N9': { '*': 523.6 },                                                      // Tan Seat Belts
  '55A': { gsx_coupe_3lz: 875.6, gsx_conv_3lz: 875.6 },                       // Competition Yellow Heritage
  '75A': { gsx_coupe_3lz: 875.6, gsx_conv_3lz: 875.6 },                       // Torch Red Heritage Hashmarks
  '97A': { gsx_coupe_3lz: 875.6, gsx_conv_3lz: 875.6 },                       // Carbon Flash Heritage
  AE4: { coupe_2lt: 1843.6, coupe_3lt: 523.6, conv_2lt: 523.6, gsx_coupe_3lz: 523.6, gsx_conv_3lz: 523.6 },// Competition Sport Seats
  AH2: { coupe_2lt: 1491.6 },                                                 // Gt2 Bucket Seats
  B6P: { coupe_2lt: 1579.6, coupe_3lt: 1579.6, gsx_coupe_3lz: 1579.6 },       // Coupe Engine Appearance Pkg
  BAZ: { '*': 611.6 },                                                        // Stealth Interior Trim Package
  BC4: { '*': 523.6 },                                                        // Engine Cover In Blue
  BCP: { '*': 523.6 },                                                        // Engine Cover In Red
  BCS: { '*': 523.6 },                                                        // Engine Cover In Sterling Silver
  C2Z: { coupe_2lt: 2283.6, coupe_3lt: 2283.6, gsx_coupe_3lz: 2283.6 },       // Visible Carbon Fiber Removable
  CFV: { gsx_coupe_3lz: 3955.6, gsx_conv_3lz: 3955.6 },                       // Ground Effects-Carbon Fiber
  CFZ: { gsx_coupe_3lz: 3075.6, gsx_conv_3lz: 3075.6 },                       // Ground Effects - Carbon Fiber
  D84: { conv_2lt: 1139.6, gsx_conv_3lz: 1139.6 },                            // Carbon Flash Painted Nacelles
  D86: { conv_2lt: 1139.6, gsx_conv_3lz: 1139.6 },                            // Carbon Flash Painted Nacelles
  DPB: { '*': 1139.6 },                                                       // Carbon Flash With Blue Accent
  DPC: { '*': 1139.6 },                                                       // Carbon Flash With Yellow
  DPG: { '*': 1139.6 },                                                       // Carbon Flash With Orange
  DPL: { '*': 1139.6 },                                                       // Carbon Flash W/ Red Accent
  DPT: { '*': 1139.6 },                                                       // Carbon Flash W/ Silver Accent
  DSY: { '*': 1139.6 },                                                       // Edge Orange Full Length
  DSZ: { '*': 1139.6 },                                                       // Edge Red Full Length
  DT0: { '*': 1139.6 },                                                       // Competition Yellow Full Length
  DTH: { '*': 1139.6 },                                                       // Carbon Flash Full Length
  DUB: { '*': 1139.6 },                                                       // Sterling Silver Full Length
  DUE: { '*': 1139.6 },                                                       // Asymmetrical Santorini Blue
  DUK: { '*': 1139.6 },                                                       // Asymmetrical Adrenaline Red/
  DX4: { gsx_coupe_3lz: 875.6, gsx_conv_3lz: 875.6 },                         // Red Mist Heritage Hash Marks
  DZU: { '*': 523.6 },                                                        // Carbon Flash/Competition Yellow
  DZV: { '*': 523.6 },                                                        // Carbon Flash/Midnight Silver
  DZX: { '*': 523.6 },                                                        // Carbon Flash/Edge Red Stinger
  EFY: { coupe_2lt: 875.6, coupe_3lt: 875.6, conv_2lt: 875.6 },               // Body Color Exterior Accents
  EPX: { coupe_3lt: 229.68, conv_2lt: 229.68, gsx_coupe_3lz: 229.68, gsx_conv_3lz: 229.68 },// Dark Atmosphere
  EYK: { '*': 347.6 },                                                        // Chrome Exterior Badge Package
  FA5: { '*': 1579.6 },                                                       // Carbon Fiber Interior Trim
  G26: { '*': 875.6 },                                                        // Sebring Orange Tintcoat
  G4Z: { '*': 440 },                                                          // Roswell Green Metallic
  G8G: { '*': 298.32 },                                                       // Arctic White
  GBK: { '*': 875.6 },                                                        // Competition Yellow Tintcoat
  GKA: { '*': 718.96 },                                                       // Silver Blade Metallic
  GKZ: { '*': 415.36 },                                                       // Torch Red
  GPH: { '*': 875.6 },                                                        // Red Mist Metallic Tintcoat
  HTG: { coupe_3lt: 229.68, conv_2lt: 229.68, gsx_coupe_3lz: 229.68, gsx_conv_3lz: 229.68 },// Natural
  HTP: { coupe_2lt: 149.6 },                                                  // Jet Black
  HTQ: { coupe_2lt: 149.6 },                                                  // Natural
  HTT: { coupe_3lt: 229.68, conv_2lt: 229.68, gsx_coupe_3lz: 229.68, gsx_conv_3lz: 229.68 },// Jet Black
  HU6: { coupe_2lt: 149.6 },                                                  // Sky Cool Gray
  HU7: { coupe_2lt: 149.6 },                                                  // Adrenaline Red
  HU9: { coupe_3lt: 229.68, conv_2lt: 229.68, gsx_coupe_3lz: 229.68, gsx_conv_3lz: 229.68 },// Sky Cool Gray
  HUA: { coupe_3lt: 229.68, conv_2lt: 229.68, gsx_coupe_3lz: 229.68, gsx_conv_3lz: 229.68 },// Adrenaline Red
  HUB: { coupe_3lt: 229.68, conv_2lt: 229.68, gsx_coupe_3lz: 229.68, gsx_conv_3lz: 229.68 },// Ultimate Suede Jet Black
  HUC: { coupe_3lt: 149.6, conv_2lt: 149.6, gsx_coupe_3lz: 149.6, gsx_conv_3lz: 149.6 },// Ultimate Leather Jet Black
  HUF: { coupe_3lt: 229.68, conv_2lt: 229.68, gsx_coupe_3lz: 229.68, gsx_conv_3lz: 229.68 },// Natural Dipped
  HUX: { coupe_3lt: 229.68, conv_2lt: 229.68, gsx_coupe_3lz: 229.68, gsx_conv_3lz: 229.68 },// Habanero
  J6B: { '*': 699.6 },                                                        // Blue Painted Brake Calipers
  J6E: { '*': 699.6 },                                                        // Yellow Painted Calipers
  J6F: { '*': 699.6 },                                                        // Bright Red Painted Brake
  J6L: { '*': 699.6 },                                                        // Orange Calipers
  J6N: { '*': 699.6 },                                                        // Calipers Edge Red Painted
  N26: { coupe_2lt: 611.6, coupe_3lt: 611.6, conv_2lt: 611.6 },               // Sueded Microfiber-Wrapped
  NWI: { '*': 347.6 },                                                        // Tailpipe Quad Exposed Bright
  Q99: { coupe_2lt: 1755.6, coupe_3lt: 1755.6, conv_2lt: 1755.6 },            // Wheels 20-Spoke Bright
  Q9A: { coupe_2lt: 1315.6, coupe_3lt: 1315.6, conv_2lt: 1315.6 },            // Wheels 20-Spoke Midnight
  Q9I: { coupe_2lt: 963.6, coupe_3lt: 963.6, conv_2lt: 963.6 },               // Wheels 20-Spoke Gloss Black
  Q9O: { coupe_2lt: 875.6, coupe_3lt: 875.6, conv_2lt: 875.6 },               // Wheels 5- Split Spoke Satin
  QE6: { coupe_2lt: 963.6, coupe_3lt: 963.6, conv_2lt: 963.6 },               // 10-Spoke Gloss Black Forged
  ROX: { gsx_coupe_3lz: 875.6, gsx_conv_3lz: 875.6 },                         // Wheels Carbon Flash Machined
  ROY: { gsx_coupe_3lz: 10555.6, gsx_conv_3lz: 10555.6 },                     // Wheels Carbon Fiber
  ROZ: { gsx_coupe_3lz: 12315.6, gsx_conv_3lz: 12315.6 },                     // Wheels Visible Carbon Fiber
  SOM: { gsx_coupe_3lz: 1315.6, gsx_conv_3lz: 1315.6 },                       // Wheels Bright Polished Forged
  SON: { gsx_coupe_3lz: 963.6, gsx_conv_3lz: 963.6 },                         // Wheels Gloss Black Forged
  STZ: { gsx_coupe_3lz: 13640, gsx_conv_3lz: 13640 },                         // Wheels Visible Carbon Fiber
  TVS: { coupe_2lt: 875.6, coupe_3lt: 875.6, conv_2lt: 875.6 },               // Low Rear Spoiler And Front
  WUB: { '*': 1755.6 },                                                       // Center Quad Exhaust System
  ZYC: { '*': 259.6 },                                                        // Carbon Flash Metallic Painted
  ZZ3: { conv_2lt: 1051.6, gsx_conv_3lz: 1051.6 },                            // Engine Appearance Package
};

/** The trims the AOL schedule covers that this configurator actually ships. */
const _PRICED_VARIANTS = ['coupe_2lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'];

/**
 * USD price for `code` as offered on `variants`, or undefined when the schedule
 * prices it on none of them — which means it is standard equipment there.
 */
const _usdFor = (code: string | undefined, variants: string[]): number | undefined => {
  const byVariant = code ? _optionPriceUSD[code] : undefined;
  if (!byVariant) return undefined;
  const scope = variants.filter((v) => _PRICED_VARIANTS.includes(v));
  if (!scope.length) return undefined;
  if (byVariant['*'] !== undefined) return byVariant['*'];
  for (const v of scope) if (byVariant[v] !== undefined) return byVariant[v];
  return undefined;
};

// Local-currency price map for a USD figure, one entry per region key. Rounded
// to whole units — GCC currencies are not quoted in fils/halala at this level.
const _usdPrices = (usd: number): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const key of Object.keys(_regions)) out[key] = Math.round(usd * _regions[key].fx);
  return out;
};

// Cast through unknown to allow additional model-level UI fields (e.g., name, specifications)
export const uimetadata = {
  models: {
    Corvette: {
      variants: {
        coupe_3lt: {
          // Year sent to Salesforce in state_info.variant ("YYYY <name>"); API-only.
          leadModelYear: '2026',
          configurationId: 'c3ltcorvette01',
          name: {
            en: 'Stingray Coupe - 3LT',
            ar: 'ستينغراي الكوبيه - 3LT',
          },
          description: {
            en: '6.2L V8 RWD',
            ar: 'محرك V8 سعة 6.2 لتر دفع خلفي',
          },
          drivetrain: 'RWD',
          trim: '3LT',
          highlights: [
            { en: '6.2L V8 engine with 535 HP', ar: 'محرك V8 سعة 6.2 لتر بقوة 535 حصان' },
            { en: 'Removable carbon-fiber roof panel', ar: 'لوحة سقف قابلة للإزالة من ألياف الكربون' },
            { en: 'Standard GT2 Napa leather seats, heated and ventilated', ar: 'مقاعد GT2 قياسية بجلد نابا، مدفأة ومهواة' },
          ],
          specs: [
            { en: '6.2L V8 engine', ar: 'محرك V8 سعة 6.2 لتر' },
            { en: '8-Speed Dual Clutch Transmission', ar: 'ناقل حركة مزدوج التعشيق بـ8 سرعات' },
          ],
          image: 'images/2387/var_coupe_3lt.png',
          // MY27 MSRP in local currency (incl. VAT where applicable), keyed by
          // the `country` URL parameter — see `config.regions` for the currency
          // each key renders in. Source: "MY27 Corvette Y2XX_Marketing
          // Communication 05 May 2026", column "MY27 MSRP (LOC)".
          // `default` mirrors UAE, matching `countryCodes.default` (+971 / AE).
          // Sheet row: 1YC07 / 3LT — YC Stingray Coupe.
          price: {
            KSA: 452400,
            UAE: 429100,
            OMR: 42800,
            BHD: 46100,
            QAR: 404300,
            KW: 32000,
            LBP: 154300,
            IQD: 116573,
            JOD: 139300,
            default: 429100,
          },
          index: 0,
          // Not the boot default any more — Stingray opens on Coupe 2LT, the
          // "starting from" trim (see coupe_2lt and the stingray journey).
          isDefault: false,
          ar: {
            enable: false,
            redirection: false,
          },
          toggleTabs: {
            CONFIG:   { mobile: true,  desktop: true },
            MODEL:    { mobile: true,  desktop: true },
            EXTERIOR: { mobile: true,  desktop: true },
            INTERIOR: { mobile: true,  desktop: true },
            OPTIONS:  { mobile: true,  desktop: true },
            SUMMARY:  { mobile: true,  desktop: true },
          },
          autoIntelRPOCode: '1YC07',
        },
        gsx_coupe_3lz: {
          leadModelYear: '2026',
          configurationId: 'gsxcoupe3lz01',
          name: { en: 'GS X Coupe - 3LT', ar: 'جراند سبورت X الكوبيه - 3LT' },
          description: { en: '6.7L V8 + Electric Motor eAWD — Grand Sport', ar: 'محرك V8 سعة 6.7 لتر + محرك كهربائي، دفع رباعي كهربائي eAWD — جراند سبورت' },
          drivetrain: 'eAWD',
          trim: '3LT',
          highlights: [
            { en: 'Grand Sport wide-body with visible carbon fiber ground effects', ar: 'جراند سبورت بهيكل عريض مع تأثيرات أرضية مرئية من ألياف الكربون' },
            { en: 'LS6 6.7L V8 + front electric motor (eAWD), 721 HP combined', ar: 'محرك LS6 V8 سعة 6.7 لتر + محرك كهربائي أمامي (eAWD)، بقوة 721 حصان مجمّعة' },
            { en: 'Standard GT2 Napa leather seats, heated and ventilated', ar: 'مقاعد GT2 قياسية بجلد نابا، مدفأة ومهواة' },
          ],
          specs: [
            { en: 'LS6 6.7L V8 engine + front electric motor (eAWD)', ar: 'محرك LS6 V8 سعة 6.7 لتر + محرك كهربائي أمامي (eAWD)' },
            { en: '8-Speed Dual Clutch Transmission', ar: 'ناقل حركة مزدوج التعشيق بـ8 سرعات' },
          ],
          image: 'images/2387/var_gsx_coupe_3lz.png',
          // Sheet row: 1YG07 / 3LT — Grand SportX Coupe.
          price: {
            KSA: 680000,
            UAE: 609300,
            OMR: 60800,
            BHD: 65400,
            QAR: 574000,
            KW: 48300,
            LBP: 221500,
            IQD: 165528,
            JOD: 198500,
            default: 609300,
          },
          index: 1,
          isDefault: false,
          ar: { enable: false, redirection: false },
          toggleTabs: {
            CONFIG:   { mobile: true,  desktop: true },
            MODEL:    { mobile: true,  desktop: true },
            EXTERIOR: { mobile: true,  desktop: true },
            INTERIOR: { mobile: true,  desktop: true },
            OPTIONS:  { mobile: true,  desktop: true },
            SUMMARY:  { mobile: true,  desktop: true },
          },
          autoIntelRPOCode: '1YG07',
        },
        conv_2lt: {
          leadModelYear: '2026',
          configurationId: 'conv2lt01',
          name: { en: 'Stingray Convertible - 3LT', ar: 'ستينغراي المكشوف - 3LT' },
          description: { en: '6.2L V8 RWD', ar: 'محرك V8 سعة 6.2 لتر دفع خلفي' },
          drivetrain: 'RWD',
          trim: '3LT',
          highlights: [
            { en: 'Retractable hardtop convertible roof', ar: 'سقف كابريوليه صلب قابل للطي' },
            { en: '6.2L V8 engine', ar: 'محرك V8 سعة 6.2 لتر' },
            { en: 'GT2 Napa leather seats, heated and ventilated', ar: 'مقاعد GT2 بجلد نابا، مدفأة ومهواة' },
          ],
          specs: [
            { en: '6.2L V8 engine', ar: 'محرك V8 سعة 6.2 لتر' },
            { en: '8-Speed Dual Clutch Transmission', ar: 'ناقل حركة مزدوج التعشيق بـ8 سرعات' },
          ],
          image: 'images/2387/var_conv_2lt.png',
          // Sheet row: 1YC67 / 3LT — YC Stingray Convertible. (The variant key
          // says `conv_2lt` but this car is the 3LT convertible — see `trim`.)
          price: {
            KSA: 482200,
            UAE: 455800,
            OMR: 45500,
            BHD: 49000,
            QAR: 429400,
            KW: 34300,
            LBP: 164200,
            IQD: 123827,
            JOD: 148000,
            default: 455800,
          },
          index: 2,
          isDefault: false,
          ar: { enable: false, redirection: false },
          toggleTabs: {
            CONFIG:   { mobile: true,  desktop: true },
            MODEL:    { mobile: true,  desktop: true },
            EXTERIOR: { mobile: true,  desktop: true },
            INTERIOR: { mobile: true,  desktop: true },
            OPTIONS:  { mobile: true,  desktop: true },
            SUMMARY:  { mobile: true,  desktop: true },
          },
          autoIntelRPOCode: '1YC67',
        },
        gsx_conv_3lz: {
          leadModelYear: '2026',
          configurationId: 'gsxconv3lz01',
          name: { en: 'GS X Convertible - 3LT', ar: 'جراند سبورت X المكشوف - 3LT' },
          description: { en: '6.7L V8 + Electric Motor eAWD — Grand Sport', ar: 'محرك V8 سعة 6.7 لتر + محرك كهربائي، دفع رباعي كهربائي eAWD — جراند سبورت' },
          drivetrain: 'eAWD',
          trim: '3LT',
          highlights: [
            { en: 'Grand Sport wide-body convertible', ar: 'جراند سبورت مكشوفة بهيكل عريض' },
            { en: 'LS6 6.7L V8 + front electric motor (eAWD), 721 HP combined', ar: 'محرك LS6 V8 سعة 6.7 لتر + محرك كهربائي أمامي (eAWD)، بقوة 721 حصان مجمّعة' },
            { en: 'GT2 Napa leather seats, heated and ventilated', ar: 'مقاعد GT2 بجلد نابا، مدفأة ومهواة' },
          ],
          specs: [
            { en: 'LS6 6.7L V8 engine + front electric motor (eAWD)', ar: 'محرك LS6 V8 سعة 6.7 لتر + محرك كهربائي أمامي (eAWD)' },
            { en: '8-Speed Dual Clutch Transmission', ar: 'ناقل حركة مزدوج التعشيق بـ8 سرعات' },
          ],
          image: 'images/2387/var_gsx_conv_3lz.png',
          // Sheet row: 1YG67 / 3LT — Grand SportX Convertible.
          price: {
            KSA: 709500,
            UAE: 635700,
            OMR: 63400,
            BHD: 68300,
            QAR: 598900,
            KW: 50400,
            LBP: 231400,
            IQD: 172700,
            JOD: 207200,
            default: 635700,
          },
          index: 3,
          isDefault: false,
          ar: { enable: false, redirection: false },
          toggleTabs: {
            CONFIG:   { mobile: true,  desktop: true },
            MODEL:    { mobile: true,  desktop: true },
            EXTERIOR: { mobile: true,  desktop: true },
            INTERIOR: { mobile: true,  desktop: true },
            OPTIONS:  { mobile: true,  desktop: true },
            SUMMARY:  { mobile: true,  desktop: true },
          },
          autoIntelRPOCode: '1YG67',
        },
        coupe_1lt: {
          leadModelYear: '2026',
          configurationId: 'coupe1lt01',
          name: { en: 'Stingray Coupe - 1LT', ar: 'ستينغراي الكوبيه - 1LT' },
          description: { en: '6.2L V8 RWD', ar: 'محرك V8 سعة 6.2 لتر دفع خلفي' },
          drivetrain: 'RWD',
          trim: '1LT',
          highlights: [
            { en: '6.2L V8 engine', ar: 'محرك V8 سعة 6.2 لتر' },
            { en: 'Removable roof panel (body-color, carbon fiber or transparent)', ar: 'لوح سقفي قابل للإزالة (بلون الهيكل أو ألياف الكربون أو شفاف)' },
            { en: 'GT1 Mulan leather seats', ar: 'مقاعد GT1 بجلد مولان' },
          ],
          specs: [
            { en: '6.2L V8 engine', ar: 'محرك V8 سعة 6.2 لتر' },
            { en: '8-Speed Dual Clutch Transmission', ar: 'ناقل حركة مزدوج التعشيق بـ8 سرعات' },
          ],
          image: 'images/2387/var_coupe_1lt.png',
          // TODO(pricing): the MY27 sheet publishes no 1LT row (Stingray Coupe
          // ships as 2LT/3LT only), so this is still the old placeholder and has
          // no regional breakdown. Harmless today because the variant is hidden
          // (`display: false`); needs real per-region prices before it is shown.
          price: { UAE: 646700, default: 646700 },
          index: 4,
          isDefault: false,
          // Hidden from the model list: the SDK still exposes this variant id, but
          // the UI filters out variants marked `display: false` (see ConfigPanel /
          // Presentation) so the Coupe 1LT card is not shown.
          display: false,
          ar: { enable: false, redirection: false },
          toggleTabs: {
            CONFIG:   { mobile: true,  desktop: true },
            MODEL:    { mobile: true,  desktop: true },
            EXTERIOR: { mobile: true,  desktop: true },
            INTERIOR: { mobile: true,  desktop: true },
            OPTIONS:  { mobile: true,  desktop: true },
            SUMMARY:  { mobile: true,  desktop: true },
          },
        },
        coupe_2lt: {
          leadModelYear: '2026',
          configurationId: 'c2ltcorvette01',
          name: { en: 'Stingray Coupe - 2LT', ar: 'ستينغراي الكوبيه - 2LT' },
          description: { en: '6.2L V8 RWD', ar: 'محرك V8 سعة 6.2 لتر دفع خلفي' },
          drivetrain: 'RWD',
          trim: '2LT',
          highlights: [
            { en: '6.2L V8 engine', ar: 'محرك V8 سعة 6.2 لتر' },
            { en: 'Removable roof panel (body-color or carbon fiber)', ar: 'لوح سقفي قابل للإزالة (بلون الهيكل أو ألياف الكربون)' },
            { en: 'GT1 Mulan leather seats', ar: 'مقاعد GT1 بجلد مولان' },
          ],
          specs: [
            { en: '6.2L V8 engine', ar: 'محرك V8 سعة 6.2 لتر' },
            { en: '8-Speed Dual Clutch Transmission', ar: 'ناقل حركة مزدوج التعشيق بـ8 سرعات' },
          ],
          image: 'images/2387/var_coupe_2lt.png',
          // Sheet row: 1YC07 / 2LT — YC Stingray Coupe.
          price: {
            KSA: 424100,
            UAE: 386200,
            OMR: 38500,
            BHD: 41500,
            QAR: 363800,
            KW: 30100,
            LBP: 138200,
            IQD: 104919,
            JOD: 125100,
            default: 386200,
          },
          index: 5,
          // Boot default: the Stingray Coupe "starting from" price (AED 386,200)
          // is this trim, so the configurator opens on it.
          isDefault: true,
          ar: { enable: false, redirection: false },
          toggleTabs: {
            CONFIG:   { mobile: true,  desktop: true },
            MODEL:    { mobile: true,  desktop: true },
            EXTERIOR: { mobile: true,  desktop: true },
            INTERIOR: { mobile: true,  desktop: true },
            OPTIONS:  { mobile: true,  desktop: true },
            SUMMARY:  { mobile: true,  desktop: true },
          },
          autoIntelRPOCode: '1YC07',
        },
      },
      backgrounds: {
        racingTrack: {
          image: 'images/2387/racing_track.png',
          title: { en: 'Racing Track', ar: 'حلبة سباق' },
        },
        cityLandscape2: {
          image: 'images/2387/city_landscape_2.png',
          title: { en: 'City Landscape', ar: 'مشهد المدينة' },
        },
      },
      // Per-variant dome skus. The App syncs metadata.backgrounds[id].sku to the current
      // variant's values on variant change, so the SDK's own background switch loads the
      // right dome (Stingray vs GSX) without a second fetch.
      backgroundVariantSkus: {
        coupe_1lt: { racingTrack: '6926', cityLandscape2: '6969' },
        coupe_2lt: { racingTrack: '6926', cityLandscape2: '6969' },
        coupe_3lt: { racingTrack: '6926', cityLandscape2: '6969' },
        conv_2lt: { racingTrack: '6926', cityLandscape2: '6969' },
        gsx_coupe_3lz: { racingTrack: '6929', cityLandscape2: '6970' },
        gsx_conv_3lz: { racingTrack: '6929', cityLandscape2: '6970' },
      },
      // Interior-colour RPO codes, resolved per variant (model) + colour id, since the
      // same colour has different codes across models and seat trims. See _interiorColorRPO.
      interiorColorRPO: _interiorColorRPO,
      ambientLighting: {},
      colors: {
        interior: {
          intJetBlack: {
            label: { en: 'Jet Black, Napa leather with perforated inserts', ar: 'أسود، جلد نابا مع لمسات مثقبة' },
            image: 'images/2387/colors/int/JetBlack_Napa_Leather_Coupe.jpg',
            usdz: 'interior_black',
            price: { default: 0 },
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
          },
          intSkyCoolGray: {
            label: { en: 'Sky Cool Gray, Napa leather with perforated inserts', ar: 'رمادي، جلد نابا مع لمسات مثقبة' },
            image: 'images/2387/colors/int/Sky_Cool_Grey_Napa_Leather_Coupe.jpg',
            usdz: 'interior_gray',
            price: { default: 0 },
            index: 1,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
          },
          intNatural: {
            label: { en: 'Natural, Napa leather with perforated inserts', ar: 'بني، جلد نابا مع لمسات مثقبة' },
            image: 'images/2387/colors/int/Natural_Napa_Leather_Coupe.jpg',
            usdz: 'interior_natural',
            price: { default: 0 },
            index: 2,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
          },
          intAdrenalineRed: {
            label: { en: 'Adrenaline Red, Napa leather with perforated inserts', ar: 'أحمر، جلد نابا مع لمسات مثقبة' },
            image: 'images/2387/colors/int/Adrenaline_Red_Napa_Leather_Coupe.jpg',
            usdz: 'interior_red',
            price: { default: 0 },
            index: 3,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
          },
          intSantoriniBlue: {
            label: { en: 'Santorini Blue, Napa leather with perforated inserts', ar: 'أزرق، جلد نابا مع لمسات مثقبة' },
            image: 'images/2387/colors/int/Santorini_Blue_Napa_Leather_Coupe.jpg',
            usdz: 'interior_blue',
            price: { default: 0 },
            index: 4,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
          },
          intNaturalDipped: {
            label: { en: 'Natural Dipped, Napa leather with perforated inserts', ar: 'بني داكن، جلد نابا مع لمسات مثقبة' },
            image: 'images/2387/colors/int/Natural_Dipped_Napa_Leather_Coupe.jpg',
            usdz: 'interior_natural',
            price: { default: 0 },
            index: 5,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
          },
          intAdrenalineRedDipped: {
            label: { en: 'Adrenaline Red Dipped, Napa leather with perforated inserts', ar: 'أحمر داكن، جلد نابا مع لمسات مثقبة' },
            image: 'images/2387/colors/int/Adrenaline_Red_Dipped_Napa_Leather_Coupe.jpg',
            usdz: 'interior_red',
            price: { default: 0 },
            index: 6,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
          },
          intAsymmetricalAdrRed: {
            label: { en: 'Asymmetrical Adrenaline Red / Jet Black, Napa leather with perforated inserts', ar: 'أحمر / أسود، جلد نابا مع لمسات مثقبة' },
            image: 'images/2387/colors/int/Asymmetrical_Adrenaline_Red_Napa_Leather_Coupe.jpg',
            usdz: 'interior_red',
            price: { default: 0 },
            index: 7,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
          },
          intHabanero: {
            label: { en: 'Habanero, Napa leather with perforated inserts', ar: 'برتقالي، جلد نابا مع لمسات مثقبة' },
            image: 'images/2387/colors/int/Habenera_Napa_Leather_Coupe.jpg',
            usdz: 'interior_natural',
            price: { default: 0 },
            index: 8,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
          },
          intVeryDarkAtmosphere: {
            label: { en: 'Very Dark Atmosphere, Napa leather with perforated inserts', ar: 'رمادي داكن جداً، جلد نابا مع لمسات مثقبة' },
            image: 'images/2387/colors/int/Very_Dark_Atmosphere_Napa_Leather_Coupe.jpg',
            usdz: 'interior_black',
            price: { default: 0 },
            index: 9,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
          },
          intUltimateSuedeJetBlack: {
            label: { en: 'Ultimate Suede Jet Black, Napa leather with perforated inserts', ar: 'جلد أسود ناعم فاخر، جلد نابا مع لمسات مثقبة' },
            image: 'images/2387/colors/int/Ultimate_Suede_JetBlack_Napa_Leather_Coupe.jpg',
            usdz: 'interior_black',
            price: { default: 0 },
            index: 10,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
          },
          intAsymSantoriniJetBlack: {
            label: { en: 'Asymmetrical Santorini Blue / Jet Black, Napa leather with perforated inserts', ar: 'أزرق / أسود، جلد نابا مع لمسات مثقبة' },
            image: 'images/2387/colors/int/Asymmetrical_Adrenaline_Blue_Napa_Leather_Coupe.jpg',
            usdz: 'interior_blue',
            price: { default: 0 },
            index: 11,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
          },
          ..._convInteriorColorUI,
          ..._suededInteriorColorUI,
          int1ltJetBlackMulan: { label: { en: 'Jet Black, Mulan leather', ar: 'أسود، جلد مولان' }, image: 'images/2387/colors/int/JetBlack_Mulan_Leather_Coupe.jpg', usdz: '', price: { default: 0 }, index: 0, type: 'interior', cardType: 'cart', cartImage: '', hasQuantity: false },
          int1ltSkyCoolGrayMulan: { label: { en: 'Sky Cool Gray, Mulan leather', ar: 'رمادي، جلد مولان' }, image: 'images/2387/colors/int/Sky_Cool_Grey_Mulan_Leather_Coupe.jpg', usdz: '', price: { default: 0 }, index: 1, type: 'interior', cardType: 'cart', cartImage: '', hasQuantity: false },
          int1ltAdrenalineRedMulan: { label: { en: 'Adrenaline Red, Mulan leather', ar: 'أحمر، جلد مولان' }, image: 'images/2387/colors/int/Adrenaline_Red_Mulan_Leather_Coupe.jpg', usdz: '', price: { default: 0 }, index: 2, type: 'interior', cardType: 'cart', cartImage: '', hasQuantity: false },
          int1ltJetBlackPerformance: { label: { en: 'Jet Black, Performance textile', ar: 'أسود، أقمشة الأداء' }, image: 'images/2387/colors/int/JetBlack_Performance_Coupe.jpg', usdz: '', price: { default: 0 }, index: 3, type: 'interior', cardType: 'cart', cartImage: '', hasQuantity: false },
          int2ltJetBlackMulan: { label: { en: 'Jet Black, Mulan leather with perforated inserts', ar: 'أسود، جلد مولان مع لمسات مثقبة' }, image: 'images/2387/colors/int/JetBlack_Mulan_Leather_Coupe.jpg', usdz: '', price: { default: 0 }, index: 0, type: 'interior', cardType: 'cart', cartImage: '', hasQuantity: false },
          int2ltSkyCoolGrayMulan: { label: { en: 'Sky Cool Gray, Mulan leather with perforated inserts', ar: 'رمادي، جلد مولان مع لمسات مثقبة' }, image: 'images/2387/colors/int/Sky_Cool_Grey_Mulan_Leather_Coupe.jpg', usdz: '', price: { default: 0 }, index: 1, type: 'interior', cardType: 'cart', cartImage: '', hasQuantity: false },
          int2ltAdrenalineRedMulan: { label: { en: 'Adrenaline Red, Mulan leather with perforated inserts', ar: 'أحمر، جلد مولان مع لمسات مثقبة' }, image: 'images/2387/colors/int/Adrenaline_Red_Mulan_Leather_Coupe.jpg', usdz: '', price: { default: 0 }, index: 2, type: 'interior', cardType: 'cart', cartImage: '', hasQuantity: false },
          int2ltNaturalMulan: { label: { en: 'Natural, Mulan leather with perforated inserts', ar: 'بني، جلد مولان مع لمسات مثقبة' }, image: 'images/2387/colors/int/Natural_Mulan_Leather_Coupe.jpg', usdz: '', price: { default: 0 }, index: 3, type: 'interior', cardType: 'cart', cartImage: '', hasQuantity: false },
        },
        exterior: {
          extArcticWhite: {
            label: { en: 'Arctic White', ar: 'أبيض' },
            image: 'images/2387/colors/ext/Arctic_White.png',
            usdz: 'white',
            price: { default: 0 },
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2387/colors/ext/Arctic_White.png',
            hasQuantity: false,
            autoIntelRPOCode: 'G8G',
          },
          extBlack: {
            label: { en: 'Black', ar: 'أسود' },
            image: 'images/2387/colors/ext/Black.png',
            usdz: 'black',
            price: { default: 0 },
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2387/colors/ext/Black.png',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX02',
          },
          extTorchRed: {
            label: { en: 'Torch Red', ar: 'أحمر' },
            image: 'images/2387/colors/ext/Torch_Red.png',
            usdz: 'red',
            price: { default: 0 },
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2387/colors/ext/Torch_Red.png',
            hasQuantity: false,
            autoIntelRPOCode: 'GKZ',
          },
          extCompetitionYellow: {
            label: { en: 'Competition Yellow Metallic Tintcoat', ar: 'أصفر داكن معدني' },
            image: 'images/2387/colors/ext/Competition_Yellow_Metallic_Tintcoat.png',
            usdz: 'yellow',
            price: { default: 0 },
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2387/colors/ext/Competition_Yellow_Metallic_Tintcoat.png',
            hasQuantity: false,
            autoIntelRPOCode: 'GBK',
          },
          extSebringOrange: {
            label: { en: 'Sebring Orange Tintcoat', ar: 'برتقالي داكن معدني' },
            image: 'images/2387/colors/ext/Sebring_Orange_Tintcoat.png',
            usdz: 'orange',
            price: { default: 0 },
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2387/colors/ext/Sebring_Orange_Tintcoat.png',
            hasQuantity: false,
            autoIntelRPOCode: 'G26',
          },
          extRoswellGreen: {
            label: { en: 'Roswell Green Metallic', ar: 'أخضر معدني' },
            image: 'images/2387/colors/ext/Roswell_Green_Metallic.png',
            usdz: 'green',
            price: { default: 0 },
            index: 6,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2387/colors/ext/Roswell_Green_Metallic.png',
            hasQuantity: false,
            autoIntelRPOCode: 'G4Z',
          },
          extBladeSilver: {
            label: { en: 'Blade Silver Metallic', ar: 'فضي معدني' },
            image: 'images/2387/colors/ext/Blade_Silver_Metallic.png',
            usdz: 'silver',
            price: { default: 0 },
            index: 7,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2387/colors/ext/Blade_Silver_Metallic.png',
            hasQuantity: false,
            autoIntelRPOCode: 'GKA',
          },
          extCrimsonVelocityRed: {
            label: { en: 'Crimson Velocity Red Metallic Tintcoat', ar: 'أحمر داكن معدني' },
            image: 'images/2387/colors/ext/Crimson_Velocity_Red_Metallic_Tintcoat.png',
            usdz: 'red',
            price: { default: 0 },
            index: 8,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2387/colors/ext/Crimson_Velocity_Red_Metallic_Tintcoat.png',
            hasQuantity: false,
            // RPO sheet lists this color as "Red Mist Metallic Tintcoat" (code GPH) — the
            // only unmatched pair once the other 9 colors were matched 1:1 by name. Flagged;
            // display label kept as-is pending confirmation this is the same color.
            autoIntelRPOCode: 'GPH',
          },
          extPitchGray: {
            label: { en: 'Pitch Gray Metallic', ar: 'رمادي داكن معدني' },
            image: 'images/2387/colors/ext/Pitch_Gray_Metallic.png',
            usdz: 'gray',
            price: { default: 0 },
            index: 9,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2387/colors/ext/Pitch_Gray_Metallic.png',
            hasQuantity: false,
            // autoIntelRPOCode pending — real RPO for Pitch Gray Metallic (sku 6997) not yet provided.
          },
          extAdmiralBlue: {
            label: { en: 'Admiral Blue Metallic', ar: 'أزرق معدني' },
            image: 'images/2387/colors/ext/Admiral_Blue_Metallic.png',
            usdz: 'blue',
            price: { default: 0 },
            index: 10,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2387/colors/ext/Admiral_Blue_Metallic.png',
            hasQuantity: false,
            // autoIntelRPOCode pending — real RPO for Admiral Blue Metallic (sku 6990) not yet provided.
          },
        },
      },
      features: {
        // === Exterior ===
        featRemovableRoof: {
          label: { en: 'Removable Roof', ar: 'لوح سقفي قابل للإزالة' },
          image: 'images/2387/features/roof.jpg',
          title: { en: 'Removable Roof', ar: 'لوح سقفي قابل للإزالة' },
          desc: { en: 'Removable carbon-fiber roof panel', ar: 'لوحة سقف قابلة للإزالة من ألياف الكربون' },
          type: 'exterior',
        },
        featFrontSuspension: {
          label: { en: 'Front Lift Suspension', ar: 'نظام رفع التعليق الأمامي' },
          image: 'images/2387/features/front_suspension.jpg',
          title: { en: 'Front Lift Suspension', ar: 'نظام رفع التعليق الأمامي' },
          desc: { en: 'Front lift suspension for added ground clearance', ar: 'نظام رفع التعليق الأمامي لزيادة الخلوص الأرضي' },
          type: 'exterior',
        },
        featExhaust: {
          label: { en: 'Performance Exhaust', ar: 'نظام عادم عالي الأداء' },
          image: 'images/2387/features/exhaust.jpg',
          title: { en: 'Performance Exhaust', ar: 'نظام عادم عالي الأداء' },
          desc: { en: '', ar: '' },
          type: 'exterior',
        },
        featExteriorDesign: {
          label: { en: 'Exterior Design', ar: 'التصميم الخارجي' },
          image: 'images/2387/features/exterior_design.jpg',
          title: { en: 'Exterior Design', ar: 'التصميم الخارجي' },
          desc: { en: '', ar: '' },
          type: 'exterior',
        },
        featWideBody: {
          label: { en: 'Wide-Body Aerodynamics', ar: 'الديناميكا الهوائية للهيكل العريض' },
          image: 'images/2387/features/wide_body.jpg',
          title: { en: 'Wide-Body Aerodynamics', ar: 'الديناميكا الهوائية للهيكل العريض' },
          desc: { en: '', ar: '' },
          type: 'exterior',
        },
        featHeadlamps: {
          label: { en: 'Automatic Leveling Headlamps', ar: 'مصابيح أمامية ذاتية التسوية' },
          image: 'images/2387/features/headlamps.jpg',
          title: { en: 'Automatic Leveling Headlamps', ar: 'مصابيح أمامية ذاتية التسوية' },
          desc: { en: '', ar: '' },
          type: 'exterior',
        },
        // === Interior ===
        featSpeaker: {
          label: { en: '14-Speaker Audio System', ar: 'نظام صوتي بـ 14 مكبر صوت' },
          image: 'images/2387/features/speaker.jpg',
          title: { en: '14-Speaker Audio System', ar: 'نظام صوتي بـ 14 مكبر صوت' },
          desc: { en: '', ar: '' },
          type: 'interior',
        },
        featHeatedSeats: {
          label: { en: 'Heated & Ventilated Seats', ar: 'مقاعد قابلة للتدفئة والتهوية' },
          image: 'images/2387/features/heated_seats.jpg',
          title: { en: 'Heated & Ventilated Seats', ar: 'مقاعد قابلة للتدفئة والتهوية' },
          desc: { en: '', ar: '' },
          type: 'interior',
        },
        featHUD: {
          label: { en: 'Head-Up Display', ar: 'نظام العرض على الزجاج الأمامي' },
          image: 'images/2387/features/hud.jpg',
          title: { en: 'Head-Up Display', ar: 'نظام العرض على الزجاج الأمامي' },
          desc: { en: '', ar: '' },
          type: 'interior',
        },
        featSteeringWheel: {
          label: { en: 'Heated Steering Wheel', ar: 'عجلة قيادة قابلة للتدفئة' },
          image: 'images/2387/features/steering.jpg',
          title: { en: 'Heated Steering Wheel', ar: 'عجلة قيادة قابلة للتدفئة' },
          desc: { en: '', ar: '' },
          type: 'interior',
        },
        featGT2Seats: {
          label: { en: 'Standard GT2 Seats with Napa Leather', ar: 'مقاعد GT2 قياسية بجلد نابا' },
          image: 'images/2387/features/gt2_seats.jpg',
          title: { en: 'Standard GT2 Seats with Napa Leather', ar: 'مقاعد GT2 قياسية بجلد نابا' },
          desc: { en: '', ar: '' },
          type: 'interior',
        },
        featInstrumentPanel: {
          label: { en: 'Leather-wrapped Instrument Panel', ar: 'لوحة عدادات مغلفة بالجلد' },
          image: 'images/2387/features/instrument_panel.jpg',
          title: { en: 'Leather-wrapped Instrument Panel', ar: 'لوحة عدادات مغلفة بالجلد' },
          desc: { en: 'Custom leather-wrapped instrument panel', ar: 'لوحة عدادات مغلفة بالجلد حسب الطلب' },
          type: 'interior',
        },
        featSuededTrim: {
          label: { en: 'Sueded Microfiber Interior Trim', ar: 'لمسات داخلية من جلد الألياف الدقيقة' },
          image: 'images/2387/features/sueded_trim.jpg',
          title: { en: 'Sueded Microfiber Interior Trim', ar: 'لمسات داخلية من جلد الألياف الدقيقة' },
          desc: { en: 'Sueded microfiber upper interior trim', ar: 'تلفيح الجزء العلوي من المقصورة بجلد الألياف الدقيقة' },
          type: 'interior',
        },
        featInstrumentPanelDoors: {
          label: { en: 'Leather-wrapped Panel & Doors', ar: 'لوحة العدادات والأبواب المغلفة بالجلد' },
          image: 'images/2387/features/instrument_panel_doors.jpg',
          title: { en: 'Leather-wrapped Instrument Panel & Doors', ar: 'لوحة العدادات والأبواب المغلفة بالجلد' },
          desc: { en: 'Custom leather-wrapped instrument panel and doors', ar: 'لوحة عدادات وأبواب مغطاة بالجلد حسب الطلب' },
          type: 'interior',
        },
      },
      accessories: {
        ..._interiorSubUI,
        accVariantParts: {
          label: { en: 'Coupe Stingray 3LT Parts', ar: 'أجزاء كوبيه ستينغراي 3LT' },
          image: '',
          category: { en: 'Accessories', ar: 'الإكسسوارات' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accVariantPartsGsxCoupe: {
          label: { en: 'GS X Coupe Parts', ar: 'أجزاء جراند سبورت X كوبيه' },
          image: '',
          category: { en: 'Accessories', ar: 'الإكسسوارات' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accCaliperBlack: {
          label: { en: 'Black Calipers', ar: 'مكابح مطلية باللون الأسود' },
          image: '',
          category: { en: 'Caliper', ar: 'المكابح' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accCaliperVelocityYellow: {
          label: { en: 'Velocity Yellow Calipers', ar: 'مكابح باللون الأصفر' },
          image: '',
          category: { en: 'Caliper', ar: 'المكابح' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accCaliperBrightRed: {
          label: { en: 'Bright Red Calipers', ar: 'مكابح مطلية باللون الأحمر الفاتح' },
          image: '',
          category: { en: 'Caliper', ar: 'المكابح' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accCaliperEdgeRed: {
          label: { en: 'Edge Red Calipers', ar: 'مكابح باللون الأحمر' },
          image: '',
          category: { en: 'Caliper', ar: 'المكابح' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accCaliperBlue: {
          label: { en: 'Blue Calipers', ar: 'مكابح مطلية باللون الأزرق' },
          image: '',
          category: { en: 'Caliper', ar: 'المكابح' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accRoofStandard: {
          label: { en: 'Standard Roof', ar: 'سقف قياسي' },
          image: '',
          category: { en: 'Roof', ar: 'السقف' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accRoofCarbonFiber: {
          label: { en: 'Visible Carbon-fiber Roof Panel', ar: 'لوحة سقف من ألياف الكربون المرئية' },
          image: '',
          category: { en: 'Roof', ar: 'السقف' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accEngineDefault: {
          label: { en: 'Engine Cover - Default', ar: 'غطاء المحرك القياسي' },
          image: '',
          category: { en: 'Engine Cover', ar: 'غطاء المحرك' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accEngineRed: {
          label: { en: 'Engine Cover in Edge Red', ar: 'غطاء المحرك باللون الأحمر' },
          image: '',
          category: { en: 'Engine Cover', ar: 'غطاء المحرك' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accEngineSilver: {
          label: { en: 'Engine Cover in Sterling Silver', ar: 'غطاء المحرك باللون الفضي' },
          image: '',
          category: { en: 'Engine Cover', ar: 'غطاء المحرك' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accEngineBlue: {
          label: { en: 'Engine Cover in Blue', ar: 'غطاء المحرك باللون الأزرق' },
          image: '',
          category: { en: 'Engine Cover', ar: 'غطاء المحرك' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accQuadExhaustBlack: {
          label: { en: 'Quad Center Exit Exhaust', ar: 'عادم رباعي بمخرج مركزي' },
          image: '',
          category: { en: 'Exhaust Tip', ar: 'أطراف العادم' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accQuadExhaustBright: {
          label: { en: 'Bright Center Quad Exposed Exhaust Tips', ar: 'أطراف عادم رباعية لامعة مكشوفة بمخرج مركزي' },
          image: '',
          category: { en: 'Exhaust Tip', ar: 'أطراف العادم' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accSpoilerDefault: {
          label: { en: 'Standard Front Splitter & Spoiler', ar: 'سبويلر ومقسم أمامي قياسي' },
          image: '',
          category: { en: 'Spoiler', ar: 'الجناح الخلفي' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accSpoilerLowProfile: {
          label: { en: 'Low Profile Package (Rear Spoiler & Front Splitter)', ar: 'باقة المظهر المنخفض (جناح خلفي ومقسم أمامي)' },
          image: '',
          category: { en: 'Packages', ar: 'الحزم' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accSpoilerAeroDelete2lt: {
          label: { en: 'Aero Delete (Z51 Performance Package)', ar: 'إلغاء العناصر الانسيابية (باقة Z51 للأداء)' },
          image: '',
          category: { en: 'Packages', ar: 'الحزم' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accAlloyQEB: {
          label: { en: '5-split-spoke Pearl Nickel forged aluminum wheels', ar: 'عجلات ألومنيوم مطروقة بخمس شعاعات مزدوجة باللون النيكل اللؤلؤي' },
          image: '',
          category: { en: 'Alloy Wheels', ar: 'عجلات ألومنيوم' },
          price: { default: 0 },
          cardType: 'cart',
          autoIntelRPOCode: 'QEB',
        },
        accAlloyQ99: {
          label: { en: '20-spoke bright machined-face forged aluminum', ar: 'عجلات ألومنيوم مطروقة بـ20 شعاعاً بواجهة مصقولة لامعة' },
          image: '',
          category: { en: 'Alloy Wheels', ar: 'عجلات ألومنيوم' },
          price: { default: 0 },
          cardType: 'cart',
          autoIntelRPOCode: 'Q99',
        },
        accAlloyQ9A: {
          label: { en: '20-spoke Midnight Gray forged aluminum with Red stripe', ar: 'عجلات ألومنيوم مطروقة بـ20 شعاعاً باللون الرمادي الداكن مع خط أحمر' },
          image: '',
          category: { en: 'Alloy Wheels', ar: 'عجلات ألومنيوم' },
          price: { default: 0 },
          cardType: 'cart',
          autoIntelRPOCode: 'Q9A',
        },
        accAlloyQ9I: {
          label: { en: '20-spoke Gloss Black forged aluminum', ar: 'عجلات ألومنيوم مطروقة بـ20 شعاعاً باللون الأسود اللامع' },
          image: '',
          category: { en: 'Alloy Wheels', ar: 'عجلات ألومنيوم' },
          price: { default: 0 },
          cardType: 'cart',
          autoIntelRPOCode: 'Q9I',
        },
        accAlloyQ9O: {
          label: { en: '5-split-spoke Satin Graphite with machined edge forged aluminum', ar: 'عجلات ألومنيوم مطروقة بخمس شعاعات مزدوجة باللون الجرافيت الساتيني مع حافة مصقولة' },
          image: '',
          category: { en: 'Alloy Wheels', ar: 'عجلات ألومنيوم' },
          price: { default: 0 },
          cardType: 'cart',
          autoIntelRPOCode: 'Q9O',
        },
        accAlloyQE6: {
          label: { en: '10-spoke Gloss Black forged aluminum wheels', ar: 'عجلات ألومنيوم مطروقة بـ10 شعاعات باللون الأسود اللامع' },
          image: '',
          category: { en: 'Alloy Wheels', ar: 'عجلات ألومنيوم' },
          price: { default: 0 },
          cardType: 'cart',
          autoIntelRPOCode: 'QE6',
        },
        accPkgStealthTrim: {
          label: { en: 'Stealth Interior Trim Package', ar: 'باقة Stealth الداخلية' },
          image: '',
          category: { en: 'Packages', ar: 'الحزم' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accPkgCarbonFiberTrim: {
          label: { en: 'Carbon Fiber Interior Trim Package', ar: 'باقة تطعيمات داخلية من ألياف الكربون' },
          image: '',
          category: { en: 'Packages', ar: 'الحزم' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accPkgEngineAppearance: {
          label: { en: 'Coupe Engine Appearance Package', ar: 'باقة مظهر المحرك لطراز الكوبيه' },
          image: '',
          category: { en: 'Packages', ar: 'الحزم' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accBadgeBlack: {
          label: { en: 'Black Logo', ar: 'شعار أسود' },
          image: '',
          category: { en: 'Logo', ar: 'الشعار' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accBadgeChrome: {
          label: { en: 'Chrome Exterior Badge Package', ar: 'باقة الشعارات الخارجية من الكروم' },
          image: '',
          category: { en: 'Packages', ar: 'الحزم' },
          price: { default: 0 },
          cardType: 'cart',
        },
        accBodyColorAccent: {
          label: { en: 'Body-color Exterior Accents', ar: 'لمسات خارجية بلون الهيكل' },
          image: '',
          category: { en: 'Packages', ar: 'الحزم' },
          price: { default: 0 },
          cardType: 'cart',
        },
      },
      packages: {},
      interactions: {
        intrDoors: {
          label: { en: 'Doors', ar: 'الأبواب' },
          type: 'interaction',
        },
        intrBoot: {
          label: { en: 'Boot', ar: 'صندوق المركبة' },
          type: 'interaction',
        },
        intrRoof: {
          label: { en: 'Roof', ar: 'السقف' },
          type: 'interaction',
        },
        intrHeadlight: {
          label: { en: 'Headlight', ar: 'المصابيح الأمامية' },
          type: 'interaction',
        },
        intrDRL: {
          label: { en: 'DRL', ar: 'مصابيح النهار' },
          type: 'interaction',
        },
        intrTaillight: {
          label: { en: 'Taillight', ar: 'المصباح الخلفي' },
          type: 'interaction',
        },
      },
      hotspots: {
        exteriorCamera: {
          title: { en: 'Exterior', ar: 'التصميم الخارجي' },
        },
        // Corvette is a 2-seater (single row), so only one interior hotspot. With a
        // single interior hotspot the Viewport hides the row dropdown (length > 1 gate).
        interiorCamera: {
          title: { en: 'Seat', ar: 'مقعد' },
        },
      },
      interiorConfig: _interiorConfig,
      name: {
        en: 'Chevrolet Corvette',
        ar: 'شفروليه كورفيت',
      },
      specifications: {
        heading: {
          en: 'Chevrolet Corvette',
          ar: 'شفروليه كورفيت',
        },
        trim: {
          en: 'Stingray Coupe - 2LT',
          ar: 'ستينغراي الكوبيه - 2LT',
        },
        price: {
          en: '',
          ar: '',
        },
        model: 'Chevrolet Corvette',
        seatingCapacity: '2',
        dimensions: {
          length: '',
          width: '',
          height: '',
          wheelbase: '',
          groundClearance: '',
        },
        performance: [
          {
            value: '535',
            unit: 'HP',
            label: { en: 'Horsepower', ar: 'القوة الحصانية' },
          },
          {
            value: '705',
            unit: 'NM',
            label: { en: 'Torque', ar: 'عزم الدوران' },
          },
          {
            value: 'V8',
            unit: '',
            label: { en: 'Engine', ar: 'المحرك' },
          },
        ],
        summary: [
          {
            title: { en: 'Model', ar: 'الطراز' },
            // This model-level block IS the Stingray data — the GS X journey
            // replaces `summary` wholesale (see journeys below), so naming the
            // car here only ever shows under Stingray. Matches the Stingray
            // journey's `heading` override.
            subtitle: { en: 'Chevrolet Corvette Stingray', ar: 'شفروليه كورفيت ستينغراي' },
            price: { default: 0 },
          },
          {
            title: { en: 'Engine', ar: 'المحرك' },
            subtitle: { en: '6.7L V8 DI engine', ar: 'محرك V8 سعة 6.7 لتر بتقنية الحقن المباشر' },
            price: { default: 0 },
          },
          {
            title: { en: 'Transmission', ar: 'ناقل الحركة' },
            subtitle: { en: '8-Speed Dual Clutch Transmission', ar: 'ناقل حركة مزدوج التعشيق بـ8 سرعات' },
            price: { default: 0 },
          },
          {
            title: { en: 'Acceleration (0-100 km/h)', ar: 'التسارع (من 0 إلى 100 كم/ساعة)' },
            subtitle: { en: '2.9 secs', ar: '2.9 ثانية' },
            superscript: '*',
            price: { default: 0 },
          },
          {
            title: { en: 'Quarter Mile', ar: 'ربع ميل' },
            subtitle: { en: '11 secs', ar: '11 ثانية' },
            superscript: '*',
            price: { default: 0 },
          },
          {
            title: { en: 'Seating Capacity', ar: 'سعة المقاعد' },
            subtitle: { en: '2', ar: '2' },
            price: { default: 0 },
          },
          {
            title: {
              en: 'Infotainment Screen',
              ar: 'شاشة المعلومات والترفيه',
            },
            subtitle: {
              en: '12.7-inch diagonal center console display, a 14-inch diagonal Driver Information Center and a 6.6-inch diagonal touchscreen auxiliary display',
              ar: 'نظام معلوماتي ترفيهي مع شاشة وسطية قياس 12.7 إنش، ومركز معلومات القيادة قياس 14 إنش وشاشة لمس مساعدة قياس 6.6 إنش',
            },
            price: {
              default: 0,
            },
          },
        ],
      },
      // ── Presentation-mode journeys (Stingray vs GS X) ───────────────────────
      // The presentation screen shows one card per journey; picking one filters the
      // configurator to that journey's variants and swaps the heading / 3 highlight
      // pointers / spec-details panel. Membership is explicit (variantIds), not a
      // hardcoded id-prefix check — see src/utils/selectedJourney.ts.
      journeys: [
        {
          key: 'stingray',
          name: { en: 'Stingray', ar: 'ستينغراي' },
          image: 'images/2387/var_coupe_3lt.png',
          defaultVariant: 'coupe_2lt',
          variantIds: ['coupe_3lt', 'conv_2lt', 'coupe_1lt', 'coupe_2lt'],
          // Presentation-mode hero video + loading slider for this journey. The
          // LoadingProgress screen prefers these over `config.presentation.*`, so
          // switching Stingray ⇄ GS X swaps the film and the slider still.
          // `videoMobile` is the phone cut; both are required per journey.
          video: 'images/2387/presentation_mode_stingray.webm',
          videoMobile: 'images/2387/phone_presentation_mode_stingray.webm',
          loadingImage: 'images/2387/desktop_slider_stingray.png',
          // Overrides the presentation heading to "Chevrolet Corvette Stingray";
          // the rest of the specs inherit from the model-level block above.
          specifications: {
            heading: { en: 'Chevrolet Corvette Stingray', ar: 'شفروليه كورفيت ستينغراي' },
          },
        },
        {
          key: 'gsx',
          name: { en: 'GS X', ar: 'جراند سبورت X' },
          image: 'images/2387/var_gsx_coupe_3lz.png',
          defaultVariant: 'gsx_coupe_3lz',
          variantIds: ['gsx_coupe_3lz', 'gsx_conv_3lz'],
          video: 'images/2387/presentation_mode_gsx.webm',
          videoMobile: 'images/2387/phone_presentation_mode_gsx.webm',
          loadingImage: 'images/2387/desktop_slider_gsx.jpg',
          // GSX overrides heading/trim, the 3 presentation highlight pointers,
          // and the full spec-details summary.
          specifications: {
            heading: { en: 'Chevrolet Corvette GS X', ar: 'شفروليه كورفيت جراند سبورت X' },
            trim: { en: 'GS X Coupe - 3LT', ar: 'جراند سبورت X الكوبيه - 3LT' },
            performance: [
              { value: '721', unit: 'HP', label: { en: 'Combined Horsepower', ar: 'القوة الحصانية المجمّعة' } },
              { value: '721', unit: 'NM', label: { en: 'Torque', ar: 'عزم الدوران' } },
              { value: 'LS6 6.7L V8', unit: '', label: { en: 'Engine', ar: 'المحرك' } },
            ],
            summary: [
              {
                title: { en: 'Model', ar: 'الطراز' },
                subtitle: { en: 'Chevrolet Corvette GSX', ar: 'شفروليه كورفيت جراند سبورت X' },
                price: { default: 0 },
              },
              {
                title: { en: 'Engine + Electric Motor eAWD', ar: 'المحرك + المحرك الكهربائي eAWD' },
                subtitle: { en: 'LS6 6.7L V8 engine', ar: 'محرك LS6 V8 سعة 6.7 لتر' },
                price: { default: 0 },
              },
              {
                // GS X is the only all-wheel-drive Corvette here — every Stingray is RWD.
                title: { en: 'Powertrain', ar: 'مجموعة نقل الحركة' },
                subtitle: { en: 'AWD (All-Wheel Drive)', ar: 'AWD (دفع رباعي)' },
                price: { default: 0 },
              },
              {
                title: { en: 'Transmission', ar: 'ناقل الحركة' },
                subtitle: { en: '8-Speed Dual Clutch Transmission', ar: 'ناقل حركة مزدوج التعشيق بـ8 سرعات' },
                price: { default: 0 },
              },
              {
                title: { en: 'Acceleration (0-100 km/h)', ar: 'التسارع (من 0 إلى 100 كم/ساعة)' },
                subtitle: { en: '2.5 secs', ar: '2.5 ثانية' },
                superscript: '*',
                price: { default: 0 },
              },
              {
                title: { en: 'Quarter Mile', ar: 'ربع ميل' },
                subtitle: { en: '10.4 secs', ar: '10.4 ثانية' },
                superscript: '*',
                price: { default: 0 },
              },
              {
                title: { en: 'Seating Capacity', ar: 'سعة المقاعد' },
                subtitle: { en: '2', ar: '2' },
                price: { default: 0 },
              },
              {
                title: { en: 'Infotainment Screen', ar: 'شاشة المعلومات والترفيه' },
                subtitle: {
                  en: '12.7-inch diagonal center console display, a 14-inch diagonal Driver Information Center and a 6.6-inch diagonal touchscreen auxiliary display',
                  ar: 'نظام معلوماتي ترفيهي مع شاشة وسطية قياس 12.7 إنش، ومركز معلومات القيادة قياس 14 إنش وشاشة لمس مساعدة قياس 6.6 إنش',
                },
                price: { default: 0 },
              },
            ],
          },
        },
      ],
    },
  },
  plugins: {
    deferredAssetLoading: true,
  },
  staticTranslations: {
    specDetails: {
      model: {
        en: 'Model',
        ar: 'الطراز',
      },
      exteriorColours: {
        en: 'Exterior',
        ar: 'التصميم الخارجي',
      },
      interiorAmbience: {
        en: 'Interior',
        ar: 'المقصورة',
      },
      seatingCapacity: {
        en: 'Seating Capacity',
        ar: 'سعة المقاعد',
      },
      length: {
        en: 'Length',
        ar: 'الطول',
      },
      width: {
        en: 'Width',
        ar: 'العرض',
      },
      height: {
        en: 'Height',
        ar: 'الارتفاع',
      },
      wheelbase: {
        en: 'Wheelbase',
        ar: 'قاعدة العجلات',
      },
      groundClearance: {
        en: 'Ground Clearance',
        ar: 'الخلوص الأرضي',
      },
      share: {
        en: 'Share',
        ar: 'مشاركة',
      },
    },
    variantSelection: {
      title: {
        en: 'Model',
        ar: 'الطراز',
      },
      select: {
        en: 'Select',
        ar: 'تحديد',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      selectBodyStyle: { en: 'Select Body Style', ar: 'اختر نمط الهيكل' },
      availableTrims: { en: 'Select Trim', ar: 'اختر الفئة' },
      driveType: { en: 'Drive Type', ar: 'نوع القيادة' },
      model: { en: 'Model', ar: 'الطراز' },
          bodyStyle: { en: 'Body Style', ar: 'نمط الهيكل' },
      coupe: { en: 'Coupe', ar: 'كوبيه' },
      convertible: { en: 'Convertible', ar: 'مكشوف' },
      passengerCapacity: { en: 'Passenger Capacity', ar: 'سعة الركاب' },
},
    loadingScreen: {
      default: {
        en: 'Preparing the canvas for your dream car. Just a few more seconds to go!',
        ar: 'لحظات ونبدأ رحلة تصميم سيارة أحلامك... باقي ثوانٍ قليلة!',
      },
      enterConfigurator: {
        en: 'Build Your Ride',
        ar: 'صمّم سيارتك',
      },
      loading: { en: 'Loading', ar: 'جارٍ التحميل' },
      variantLoading: { en: 'Trim is loading', ar: 'جارٍ تحميل الفئة' },
      viewLoading: {
        en: 'Applying final touches',
        ar: 'جارٍ وضع اللمسات الأخيرة',
      },
      disclaimer: {
        en: 'Disclaimer',
        ar: 'إخلاء المسؤولية',
      },
      termsConditions: {
        en: 'Terms & Conditions',
        ar: 'الشروط والأحكام',
      },
      viewSpecDetails: {
        en: 'View Spec Details',
        ar: 'عرض تفاصيل المواصفات',
      },
      disclaimerContent: {
        en: 'Prices are subject to change without prior notice and may vary based on location, availability, or other factors.',
        ar: 'الأسعار عرضة للتغيير دون إشعار مسبق وقد تختلف بناءً على الموقع والتوافر وعوامل أخرى.',
      },
      disclaimerNote1: {
        en: '*On a closed course. Based on initial vehicle movement. With standard Z51 Performance Package',
        ar: '*على مضمار مغلق. بناءً على الحركة الأولية للمركبة. مع حزمة Z51 للأداء القياسية',
      },
      termsModal: {
        title: {
          en: 'Configurator Use Terms and Conditions',
          ar: 'شروط وأحكام استخدام أداة تخصيص المركبات',
        },
        lead: {
          en: 'By using this vehicle configurator and submitting your build, you acknowledge and agree to the following:',
          ar: 'من خلال استخدام أداة تخصيص المركبة هذه وإرسال تصميمك، فإنك تقر وتوافق على ما يلي:',
        },
        sections: {
          item1: {
            title: {
              en: 'Non-Binding Submission',
              ar: 'تقديم غير ملزم',
            },
            content: {
              en: 'Submitting your vehicle configuration does not constitute a formal vehicle order or purchase agreement. No payment or deposit is collected at this stage.',
              ar: 'لا يُعد إرسال تكوين مركبتك طلبًا رسميًا للشراء أو اتفاقية شراء. ولا يتم تحصيل أي دفعة أو عربون في هذه المرحلة.',
            },
          },
          item2: {
            title: {
              en: 'Preliminary Estimate',
              ar: 'تقدير أولي',
            },
            content: {
              en: 'All pricing displayed is Manufacturer’s Suggested Retail Price (MSRP)and is provided for informational purposes only. Final pricing, taxes, fees, and applicable offers will be confirmed by the authorized dealer.',
              ar: 'جميع الأسعار المعروضة هي أسعار التجزئة المقترحة من الشركة المصنّعة (MSRP)، وهي مقدّمة لأغراض إعلامية فقط. وسيؤكد الوكيل المعتمد الأسعار النهائية والضرائب والرسوم والعروض السارية.',
            },
          },
          item3: {
            title: {
              en: 'Dealer Follow-Up',
              ar: 'متابعة الوكيل',
            },
            content: {
              en: 'Your submitted configuration will be shared with a participating dealer, who will contact you to discuss your preferences, availability, and next steps.',
              ar: 'ستتم مشاركة التكوين الذي أرسلته مع وكيل مشارك، وسيتواصل معك لمناقشة تفضيلاتك والتوافر والخطوات التالية.',
            },
          },
          item4: {
            title: {
              en: 'Availability and Fulfillment',
              ar: 'التوافر وتلبية الطلبات',
            },
            content: {
              en: 'We will make every reasonable effort to fulfill your preferences; however, final vehicle and accessories selection is subject to dealer inventory and production availability. Configurations may be limited to in-stock or incoming vehicles.',
              ar: 'سنبذل كل جهدنا لتلبية تفضيلاتك؛ إلا أن اختيار المركبة والأكسسوارات النهائي يخضع لمخزون الوكيل وتوافر الإنتاج. وقد تقتصر التكوينات على المركبات المتوفرة في المخزون أو الواردة.',
            },
          },
          item5: {
            title: {
              en: 'No Obligation',
              ar: 'لا يوجد التزام',
            },
            content: {
              en: 'You are under no obligation to purchase the configured vehicle, and submitting your configuration does not create a contractual relationship between you and the manufacturer or dealer.',
              ar: 'لست ملزمًا بشراء المركبة التي قمت بتكوينها، ولا ينشئ إرسال التكوين علاقة تعاقدية بينك وبين المصنّع أو الوكيل.',
            },
          },
          item6: {
            title: {
              en: 'Use of Personal Information and Consent',
              ar: 'استخدام المعلومات الشخصية والموافقة',
            },
            content: {
              en: 'By submitting your configuration, you consent to the collection, use, and sharing of your personal information with authorized dealers to facilitate your request, in accordance with our [Privacy Statement].',
              ar: 'بإرسال تكوينك، فإنك توافق على جمع معلوماتك الشخصية واستخدامها ومشاركتها مع الوكلاء المعتمدين لتسهيل طلبك، وفقًا لـ[بيان الخصوصية] الخاص بنا.',
            },
          },
          item7: {
            title: {
              en: 'System Limitations',
              ar: 'قيود النظام',
            },
            content: {
              en: 'While we aim to provide accurate and current information, the configurator may not reflect all available features or up-to-date pricing. Errors or omissions may occur.',
              ar: 'مع أننا نهدف إلى توفير معلومات دقيقة وحديثة، إلا أن أداة تخصيص المركبات قد لا تعكس جميع المزايا المتاحة أو الأسعار المحدّثة. وقد تحدث أخطاء أو حالات سهو.',
            },
          },
        },
      },
    },
    common: {
      exit: {
        en: 'Exit',
        ar: 'خروج',
      },
      close: {
        en: 'Close',
        ar: 'إغلاق',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      back: {
        en: 'Back',
        ar: 'رجوع',
      },
      next: {
        en: 'Next',
        ar: 'التالي',
      },
      done: {
        en: 'Done',
        ar: 'تم',
      },
      exterior: {
        en: 'Exterior',
        ar: 'التصميم الخارجي',
      },
      interior: {
        en: 'Interior',
        ar: 'المقصورة',
      },
      enterFullscreen: {
        en: 'Enter Fullscreen',
        ar: 'ملء الشاشة',
      },
      exitFullscreen: {
        en: 'Exit Fullscreen',
        ar: 'إلغاء ملء الشاشة',
      },
      selectTrim: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      viewConfiguration: {
        en: 'View Configuration',
        ar: 'عرض التكوين',
      },
      togglePanelTooltip: {
        en: 'Select Trim and Configuration',
        ar: 'اختر الفئة والتكوين',
      },
      nextStepTooltip: {
        en: 'Click to proceed to the next step',
        ar: 'انقر للانتقال إلى الخطوة التالية',
      },
      heading: {
        en: 'Standard Features',
        ar: 'الميزات القياسية',
      },
      description: {
        en: 'Chevrolet Arabia',
        ar: 'شفروليه العربية',
      },
      goBack: { en: 'Go Back', ar: 'رجوع' },
      freeRoamView: { en: 'Free Roam View', ar: 'عرض التجوال الحر' },
      featureView: { en: 'Featured View', ar: 'عرض الميزات' },
      configurator: { en: 'Configurator', ar: 'أداة تخصيص المركبات' },
      buildYourRide: { en: 'Build Your Ride', ar: 'صمّم سيارتك' },
      options: { en: 'Options', ar: 'الخيارات' },
      summary: { en: 'Summary', ar: 'الملخص' },
      saveYourBuild: { en: 'Save Your Build', ar: 'احفظ تكوينك' },
      startingPrice: { en: 'Starting Price', ar: 'السعر الابتدائي' },
      msrp: { en: 'MSRP', ar: 'MSRP' },
      proceed: { en: 'Proceed', ar: 'متابعة' },
          loading: { en: 'Loading experience...', ar: 'جارٍ تحميل التجربة...' },
      remove: { en: 'Remove', ar: 'إزالة' },
},
    tabs: {
      config: { en: 'Config', ar: 'التكوين' },
      model: { en: 'Model', ar: 'الطراز' },
      exterior: { en: 'Exterior', ar: 'التصميم الخارجي' },
      interior: { en: 'Interior', ar: 'المقصورة' },
      options: { en: 'Options', ar: 'الخيارات' },
      summary: { en: 'Summary', ar: 'الملخص' },
    },
    cart: {
      title: {
        en: 'Your Order',
        ar: 'طلبك',
      },
      disclaimer: {
        en: 'Accessory prices and availability will be confirmed at the showroom.',
        ar: 'سيتم تأكيد أسعار الملحقات وتوافرها في صالة العرض.',
      },
      exteriorLabel: {
        en: 'Exterior:',
        ar: ':خارجي',
      },
      interiorLabel: {
        en: 'Interior:',
        ar: 'المقصورة:',
      },
      addedAccessories: {
        en: 'Added Accessories',
        ar: 'الإكسسوارات المضافة',
      },
      priceEstimate: {
        en: 'Price Estimate',
        ar: 'السعر التقديري',
      },
      priceBreakdown: {
        en: 'Price Breakdown',
        ar: 'تفاصيل السعر',
      },
      reserveNow: {
        en: 'Proceed',
        ar: 'متابعة',
      },
      removeAccessory: {
        en: 'Remove accessory',
        ar: 'إزالة الإكسسوار',
      },
      removeItem: { en: 'Remove', ar: 'إزالة' },
      packageCategory: { en: 'Package', ar: 'الباقة' },
    },
    saveDialog: {
      optionsTitle: {
        en: 'How would you like to proceed?',
        ar: 'كيف تريد المتابعة؟',
      },
      optionWhatsapp: { en: 'Save to WhatsApp', ar: 'حفظ عبر الواتس اب' },
      optionEmail: { en: 'Save to Email', ar: 'حفظ عبر البريد الإلكتروني' },
      optionTestDrive: {
        en: 'Book a Test Drive',
        ar: 'احجز تجربة قيادة',
      },
      optionCallback: {
        en: 'Request a call Back',
        ar: 'اطلب معاودة الاتصال',
      },
      labelFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول*',
      },
      labelLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة*',
      },
      labelEmail: {
        en: 'Email*',
        ar: 'البريد الإلكتروني*',
      },
      labelMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      labelPrefix: {
        en: 'Prefix',
        ar: 'رمز الدولة',
      },
      labelModel: {
        en: 'Model',
        ar: 'الطراز',
      },
      labelDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل*',
      },
      labelComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      labelTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      labelTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      placeholderTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول*',
      },
      placeholderLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة*',
      },
      placeholderEmail: {
        en: 'Email*',
        ar: 'البريد الإلكتروني*',
      },
      placeholderMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      placeholderComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      placeholderSelectDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل*',
      },
      privacyTitle: {
        en: 'Privacy statement & legal disclaimer',
        ar: 'بيان الخصوصية وإخلاء المسؤولية القانونية',
      },
      privacyText: {
        en: 'I acknowledge and understand that my information will be shared with General Motors, its affiliates, dealer network or other parties are required by law for compliance, safety campaigns, government inquiries, or similar legal process. My information may also be shared for product research and development purposes, and to manage customer relationships to provide support. GM’s Privacy Statement addresses how GM handles the personal information shared with us.',
        ar: 'أقرّ وأفهم أن معلوماتي قد تتم مشاركتها مع شركة جنرال موتورز والشركات التابعة لها وشبكة وكلائها أو مع جهات أخرى، حسب ما يقتضي القانون، لأغراض الامتثال وحملات السلامة والاستفسارات الحكومية أو الإجراءات القانونية المماثلة. وقد تتم أيضًا مشاركة معلوماتي لأغراض البحث والتطوير في المنتجات، ولإدارة علاقات العملاء وتقديم الدعم. يوضح بيان الخصوصية الخاص بجنرال موتورز كيفية تعامل جنرال موتورز مع المعلومات الشخصية التي تتم مشاركتها معنا.',
      },
      privacyReadMore: {
        en: 'Read more',
        ar: 'اقرأ المزيد',
      },
      privacyMarketingConsent: {
        en: 'I wish to receive any marketing information or have my information shared with third parties for purposes of providing me with marketing information.',
        ar: 'أرغب في تلقي معلومات تسويقية أو مشاركة معلوماتي مع جهات خارجية بغرض تزويدي بمعلومات تسويقية.',
      },
      buttonSend: {
        en: 'Send Configuration',
        ar: 'إرسال التكوين',
      },
      buttonSending: {
        en: 'Submitting...',
        ar: 'جارٍ الإرسال...',
      },
      buttonSent: {
        en: 'Sent!',
        ar: 'مرسل!',
      },
      buttonRetry: {
        en: 'Retry',
        ar: 'إعادة المحاولة',
      },
      buttonClose: {
        en: 'Okay, Got it',
        ar: 'حسنًا، فهمت',
      },
      successTitle: {
        en: 'Saved',
        ar: 'تم الحفظ',
      },
      successLabelModel: {
        en: 'Model Name',
        ar: 'اسم الطراز',
      },
      successLabelVariant: {
        en: 'Variant Name',
        ar: 'اسم البديل',
      },
      successLabelName: {
        en: 'Name',
        ar: 'اسم',
      },
      successLabelEmail: {
        en: 'Email*',
        ar: 'البريد الإلكتروني*',
      },
      successLabelMobile: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      qrTitle: {
        en: 'Your Unique Configuration QR',
        ar: 'رمز الاستجابة السريعة لتكوينك',
      },
      qrDescription: {
        en: 'Show this QR code to your nearest dealer to access your saved configuration.',
        ar: 'أظهر رمز الاستجابة السريعة هذا لتاجر التجزئة الأقرب للوصول إلى التكوين المحفوظ.',
      },
      qrNoteLabel: {
        en: 'Note:',
        ar: 'ملاحظة:',
      },
      qrNote: {
        en: 'Only authorized dealers can view this configuration.',
        ar: 'يمكن للوكلاء المعتمدين فقط عرض هذا التكوين.',
      },
      qrDownload: {
        en: 'Download QR',
        ar: 'تحميل QR',
      },
      qrSave: {
        en: 'Save QR',
        ar: 'حفظ QR',
      },
      errorTitle: {
        en: 'Submission Failed',
        ar: 'تعذّر الإرسال',
      },
      errorMessage: {
        en: 'We encountered an error, please try again.',
        ar: 'واجهنا خطأ، يرجى المحاولة مرة أخرى.',
      },
    },
    featureGroups: {
      exteriorDesign: {
        en: 'Exterior Design & Styling',
        ar: 'التصميم واللمسات الخارجية',
      },
      interiorComfort: {
        en: 'Interior Comfort & Features',
        ar: 'راحة المقصورة وميزاتها',
      },
      technologyConvenience: {
        en: 'Technology & Convenience',
        ar: 'التكنولوجيا ومزايا الراحة',
      },
      safetyAssistance: {
        en: 'Safety & Driver Assistance',
        ar: 'السلامة ومساعدة السائق',
      },
    },
    sidePanel: {
      menu: { en: 'Main Menu', ar: 'القائمة الرئيسية' },
      search: { en: 'Search', ar: 'بحث' },
      settings: { en: 'Settings', ar: 'الإعدادات' },
      profile: { en: 'Profile', ar: 'الملف الشخصي' },
      notifications: { en: 'Notifications', ar: 'الإشعارات' },
      help: { en: 'Help & Support', ar: 'المساعدة والدعم' },
      filter: { en: 'Filter Options', ar: 'خيارات التصفية' },
      favorites: { en: 'Favorites', ar: 'المفضلة' },
      history: { en: 'History', ar: 'التاريخ' },
      tools: { en: 'Tools', ar: 'الأدوات' },
      variants: { en: 'Variants', ar: 'الفئات' },
      exteriorColor: { en: 'Exterior Color', ar: 'اللون الخارجي' },
      interiorColor: { en: 'Interior Color', ar: 'لون المقصورة' },
      features: { en: 'Features', ar: 'الميزات' },
      step: { en: 'Step', ar: 'الخطوة' },
      openPanel: { en: 'Open Panel', ar: 'فتح اللوحة' },
      closePanel: { en: 'Close Panel', ar: 'إغلاق اللوحة' },
    },
    landscape: {
      message: {
        en: 'Please rotate your device to portrait mode',
        ar: 'يُرجى تدوير جهازك إلى الوضع العمودي',
      },
      subtitle: {
        en: 'For the best experience, please turn your device upright.',
        ar: 'لأفضل تجربة، يُرجى تدوير جهازك إلى الوضع العمودي',
      },
    },
    cameraViewToggle: {
      ext: { en: 'Ext', ar: 'التصميم الخارجي' },
      int: { en: 'Int', ar: 'المقصورة' },
      dualTone: { en: 'Dual-tone', ar: 'ثنائي اللون' },
    },
    bottomDrawer: {
      variants: { en: 'Variants', ar: 'الفئات' },
      interactions: { en: 'Interactions', ar: 'التفاعلات' },
      accessories: { en: 'Options & Accessories', ar: 'الإكسسوارات' },
      features: { en: 'Features', ar: 'الميزات' },
      highlights: { en: 'Highlights', ar: 'المزايا' },
      moreOptions: { en: 'More Options', ar: 'المزيد من الخيارات' },
      colorSelector: { en: 'Color Selector', ar: 'تحديد اللون' },
      exteriorColor: { en: 'Exterior Color', ar: 'اللون الخارجي' },
      interiorColor: { en: 'Interior Color', ar: 'لون المقصورة' },
      wheels: { en: 'Wheels', ar: 'العجلات' },
      roofs: { en: 'Roofs', ar: 'الأسقف' },
      performance: { en: 'Performance', ar: 'الأداء' },
      packages: { en: 'Packages', ar: 'الحزم' },
      chargingOptions: { en: 'Charging Options', ar: 'خيارات الشحن' },
      safetyServices: { en: 'Safety & Services', ar: 'السلامة والخدمات' },
      expand: { en: 'Expand', ar: 'توسيع' },
      minimize: { en: 'Minimize', ar: 'تصغير' },
      freeRoamView: { en: 'Free Roam View', ar: 'عرض التجوال الحر' },
      featureView: { en: 'Featured View', ar: 'عرض الميزات' },
    },
    qr: {
      scan: { en: 'Scan QR', ar: 'امسح رمز الاستجابة السريعة' },
      scanWithPhone: {
        en: 'Scan with your phone to view in your Space',
        ar: 'امسح الرمز عن طريق هاتفك لعرض المركبة في مساحتك',
      },
    },
    share: {
      title: { en: 'Share Configuration', ar: 'مشاركة التكوين' },
      description: {
        en: 'Share your vehicle configuration with others',
        ar: 'شارك تكوين سيارتك مع الآخرين',
      },
      twitterTitle: {
        en: 'Check out my Chevrolet Corvette configuration!',
        ar: 'اطّلع على تكوين شفروليه كورفيت الخاص بي!',
      },
      whatsappTitle: {
        en: 'Check out my Chevrolet Corvette configuration!',
        ar: 'اطّلع على تكوين شفروليه كورفيت الخاص بي!',
      },
      linkedinTitle: {
        en: 'My Chevrolet Corvette Configuration',
        ar: 'تكوين شفروليه كورفيت الخاص بي',
      },
      linkedinSummary: {
        en: 'Check out my custom Chevrolet Corvette configuration!',
        ar: 'اطّلع على تكويني المخصّص لشفروليه كورفيت!',
      },
      emailSubject: {
        en: 'Check out my Chevrolet Corvette Configuration',
        ar: 'اطّلع على تكوين شفروليه كورفيت',
      },
      emailBody: {
        en: 'I configured my dream Chevrolet Corvette. Take a look:',
        ar: 'خصّصت سيارة شفروليه كورفيت التي أحلم بها. ألقِ نظرة:',
      },
      linkLabel: { en: 'Or copy link:', ar: 'أو انسخ الرابط:' },
      copied: {
        en: 'Copied',
        ar: 'تم النسخ',
      },
      copy: { en: 'Copy', ar: 'نسخ' },
      saveAndShare: { en: 'Save & Share', ar: 'حفظ ومشاركة' },
      copyLink: { en: 'Copy link', ar: 'نسخ الرابط' },
      facebook: { en: 'Facebook', ar: 'Facebook' },
      twitter: { en: 'Twitter', ar: 'Twitter' },
      gmail: { en: 'Gmail', ar: 'Gmail' },
      whatsapp: { en: 'Whatsapp', ar: 'Whatsapp' },
    },
    topBar: {
      matchingVehiclesFound: {
        en: 'Matching Vehicles Found',
        ar: 'تم العثور على مركبات مطابقة',
      },
    },
    bottomBar: {
      priceLabel: { en: 'ex showroom price', ar: 'سعر صالة العرض' },
      leads: { en: 'Save', ar: 'حفظ' },
      findLocalMatches: {
        en: 'Find Local Matches',
        ar: 'اعثر على التطابقات المحلية',
      },
      save: { en: 'Save', ar: 'حفظ' },
      item: { en: 'Item', ar: 'العنصر' },
      items: { en: 'Items', ar: 'العناصر' },
      totalToPay: { en: 'Total to pay', ar: 'إجمالي المبلغ المستحق' },
      bookTestDrive: { en: 'Save', ar: 'حفظ' },
      requestQuote: { en: 'Request a Quote', ar: 'طلب عرض سعر' },
      downloadBrochure: { en: 'Download Brochure', ar: 'تحميل الكتيب' },
    },
    priceSummary: {
      title: { en: 'Price Summary', ar: 'ملخص السعر' },
      enterLocation: { en: 'Enter Location', ar: 'أدخل الموقع' },
      selectCountry: { en: 'Location', ar: 'الموقع' },
      selectCity: { en: 'Location', ar: 'الموقع' },
      note: {
        en: 'Note : Prices might vary by state and all prices listed are on road price, T&C apply.',
        ar: 'ملاحظة: قد تختلف الأسعار حسب المنطقة, تطبق الشروط والأحكام.',
      },
      breakdown: { en: 'Price Breakdown', ar: 'تفاصيل السعر' },
      color: { en: 'Color', ar: 'اللون' },
      variant: { en: 'Variant', ar: 'الفئة' },
      included: { en: 'Included', ar: 'مشمول' },
      total: { en: 'Total', ar: 'الإجمالي' },
    },
    featureDialog: {
      playPause: { en: 'Play-Pause', ar: 'تشغيل/إيقاف مؤقت' },
    },
    selectDealership: {
      title: { en: 'Select a Dealer', ar: 'اختر وكيلاً' },
      subtitle: {
        en: 'Please select a dealer to find local matches similar to your configuration',
        ar: 'يرجى اختيار وكيل للعثور على سيارات مطابقة لتكوينك في منطقتك',
      },
      city: { en: 'UAE', ar: 'الإمارات العربية المتحدة' },
      dealersIn: { en: 'Dealers in', ar: 'الوكلاء في' },
      dealersInCity: { en: 'Dealers in {{city}}', ar: 'الوكلاء في {{مدينة}}' },
      availableInLocations: {
        en: 'Available in {{count}} locations of {{city}}',
        ar: 'متوفر في {{count}} مواقع في {{city}}',
      },
      proceed: {
        en: 'Select Dealer and Proceed to Configurator',
        ar: 'اختر الوكيل وتابع إلى أداة تخصيص المركبات (المكوّن)',
      },
    },
    countryNames: {
      UAE: { en: 'UAE', ar: 'الإمارات العربية المتحدة' },
      KSA: { en: 'KSA', ar: 'المملكة العربية السعودية' },
      KW: { en: 'Kuwait', ar: 'الكويت' },
      default: { en: 'UAE', ar: 'الإمارات العربية المتحدة' },
    },
    localMatches: {
      exterior: { en: 'Exterior', ar: 'التصميم الخارجي' },
      interior: { en: 'Interior', ar: 'المقصورة' },
      dualTone: { en: 'Dual-tone', ar: 'ثنائي اللون' },
      yourConfiguration: { en: 'Current Configuration', ar: 'التكوين الحالي' },
      viewConfiguration: {
        en: 'View your configuration details',
        ar: 'عرض تفاصيل تكوينك',
      },
      yourBuildDetails: { en: 'Your Build Details', ar: 'تفاصيل تكوينك' },
      options: { en: 'Options', ar: 'الخيارات' },
      save: { en: 'Save', ar: 'حفظ' },
      dealersIn: { en: 'Dealers in', ar: 'الوكلاء في' },
      changeDealer: { en: 'Change Dealer', ar: 'تغيير الوكيل' },
      carsAvailable: {
        en: 'Cars Available at the Selected Dealership',
        ar: 'السيارات المتوفرة لدى الوكيل المختار',
      },
      matchesFound: { en: 'Matches found', ar: 'تم العثور على تطابقات' },
      startingFrom: { en: 'Starting from', ar: 'ابتداءً من' },
      getQuote: { en: 'Proceed', ar: 'متابعة' },
      viewDifferences: { en: 'View Differences', ar: 'عرض الاختلافات' },
      match: { en: 'Match', ar: 'تطابق' },
      closestMatchFound: {
        en: 'Closest Match Found',
        ar: 'أقرب تطابق تم العثور عليه',
      },
      buildDetails: { en: 'Build Details', ar: 'تفاصيل التخصيص' },
      trim: { en: 'Trim', ar: 'الفئة' },
      matchScoreTooltip: {
        en: 'This score indicates how closely this vehicle matches your configuration.',
        ar: 'تشير هذه النتيجة إلى مدى تطابق هذه المركبة مع تكوينك.',
      },
      exactMatch: { en: 'Exact Match', ar: 'تطابق تام' },
      similarMatch: { en: 'Close Match', ar: 'تطابق قريب' },
      availableNow: { en: 'Available Now', ar: 'متوفر الآن' },
      whatYouConfigured: { en: 'What you configured', ar: 'تكوينك' },
      matchingInStock: { en: 'Matching vehicle available in stock', ar: 'مركبة مطابقة متوفرة في المخزون' },
      noMatches: {
        en: 'No matching vehicles found in your area.',
        ar: 'لم يتم العثور على سيارات مطابقة في منطقتك.',
      },
      closeMatchDetails: { en: 'Close Match Details', ar: 'تفاصيل التطابق القريب' },
      matchesYourSelection: { en: 'Matches your selection', ar: 'يطابق اختيارك' },
      sameAsWhatYouSelected: { en: 'Same as what you selected', ar: 'مطابق لما اخترته' },
      replacesYourSelection: { en: 'Replaces your selection', ar: 'يستبدل اختيارك' },
      selectionNotIncluded: { en: 'Selection not included', ar: 'الاختيار غير مشمول' },
      includedInThisVehicle: { en: 'Included in this vehicle', ar: 'مشمول في هذه المركبة' },
      additionalFeatureAlreadyIncluded: { en: 'Additional feature already included', ar: 'ميزة إضافية مشمولة بالفعل' },
      stockDisclaimer: { en: 'Available stock may include extra features not selected in your original configuration.', ar: 'قد تشمل المركبات المتاحة ميزات إضافية لم تختارها في تخصيصك الأصلي.' },
      additionalInstalledOptionsLegend: { en: 'Included in this vehicle', ar: 'مشمول في هذه المركبة' },
      vehicleDetails: { en: 'Vehicle Details', ar: 'تفاصيل المركبة' },
      installedOptionsTitle: { en: 'Comes with vehicle', ar: 'يأتي مع المركبة' },
      additionalInstalledOptionsSubtitle: { en: 'Additional Installed Options:', ar: 'الخيارات الإضافية المثبّتة:' },
      specTrim: { en: 'Trim', ar: 'الفئة' },
      specExteriorColor: { en: 'Exterior Color', ar: 'اللون الخارجي' },
      specInteriorColor: { en: 'Interior Color', ar: 'لون المقصورة' },
      specWheels: { en: 'Wheels', ar: 'العجلات' },
      specPackage: { en: 'Package', ar: 'الباقة' },
      title: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
      message: {
        en: 'We Found a few local matches similar to your current configuration. Click to view Matches',
        ar: 'لقد وجدنا بعض المركبات المتوفرة محلياً والمشابهة لتخصيصك الحالي. انقر لعرض المركبات المطابقة.',
      },
          specModel: { en: 'Model', ar: 'الطراز' },
      viewDetails: { en: 'View Details', ar: 'عرض التفاصيل' },
},
    trim: {
      selection: { en: 'Trim Selection', ar: 'اختيار الفئة' },
    },
    paymentPage: {
      exteriorColor: { en: 'Exterior Color', ar: 'اللون الخارجي' },
      interiorColor: { en: 'Interior Color', ar: 'لون المقصورة' },
      accessory: { en: 'Accessory', ar: 'الإكسسوار' },
      closePayment: { en: 'Close', ar: 'إغلاق' },
    },
    packageModal: {
      includes: { en: 'Includes:', ar: 'يشمل:' },
      of: { en: 'of', ar: 'من' },
      totalPriceChange: {
        en: 'Total Price Change:',
        ar: 'إجمالي فرق السعر:',
      },
      item: { en: 'Item', ar: 'العنصر' },
      items: { en: 'Items', ar: 'العناصر' },
      addPackage: { en: 'Add Package', ar: 'إضافة باقة' },
      noThanks: { en: 'No Thanks', ar: 'لا شكراً' },
      alreadyApplied: { en: 'Already Applied', ar: 'مُطبّق بالفعل' },
    },
    changesRequired: { en: 'Changes Required', ar: 'التغييرات المطلوبة' },
    changesRequiredDescription: {
      en: 'The package you selected conflicts with your current selections. Please review the changes below.',
      ar: 'تتعارض الباقة التي اخترتها مع اختياراتك الحالية. يُرجى مراجعة التغييرات أدناه.',
    },
    itemsThatWillBeRemoved: {
      en: 'Items that will be removed',
      ar: 'العناصر التي سيتم إزالتها',
    },
    itemsThatWillBeAdded: {
      en: 'Items that will be added',
      ar: 'العناصر التي سيتم إضافتها',
    },
    applyChanges: { en: 'Apply Changes', ar: 'تطبيق التغييرات' },
    previewFeature: { en: 'Preview Feature', ar: 'معاينة الميزة' },
    exploreFeatures: { en: 'Explore Features', ar: 'استكشف الميزات' },
    buildAndPrice: { en: 'Build & Price', ar: 'خصّص مركبتك واعرف السعر' },
    features: {
      preview: { en: 'Preview Feature', ar: 'معاينة الميزة' },
      playAll: { en: 'PLAY ALL', ar: 'تشغيل الكل' },
      title: { en: 'Features', ar: 'الميزات' },
    },
    header: {
      localMatches: { en: 'Local\nMatches', ar: 'التطابقات\nالمحلية' },
      matchingVehicles: { en: 'Matching vehicles', ar: 'المركبات المطابقة' },
      configurationsFound: { en: 'Configurations Found', ar: 'تكوينات تم العثور عليها' },
    },
    configPanel: {
      playAll: { en: 'Play All', ar: 'تشغيل الكل' },
      pause: { en: 'Pause', ar: 'إيقاف مؤقت' },
      exteriorColors: { en: 'Exterior Colors', ar: 'الألوان الخارجية' },
      interiorColors: { en: 'Interior Colors', ar: 'ألوان المقصورة' },
      roofOptions: { en: 'Roof Options', ar: 'خيارات السقف' },
      interiorOptions: { en: 'Interior Options', ar: 'خيارات المقصورة' },
      exteriorAccessories: { en: 'Exterior Accessories', ar: 'إكسسوارات خارجية' },
      noAdditionalOptions: { en: 'No additional options available for the selected trim.', ar: 'لا توجد خيارات إضافية متاحة للفئة المختارة.' },
      noFilters: { en: 'No filters available.', ar: 'لا تتوفر خيارات للتصفية' },
      noVariantsMatch: { en: 'No trims match the selected filters.', ar: 'لا توجد فئات تطابق الفلاتر المختارة.' },
      configurationBreakdown: { en: 'Configuration Breakdown', ar: 'تفاصيل التكوين' },
      yourConfiguration: { en: 'Your Configuration', ar: 'تكوينك' },
      trim: { en: 'Trim', ar: 'الفئة' },
      total: { en: 'Total', ar: 'الإجمالي' },
          seat: { en: 'Seat', ar: 'المقاعد' },
      seatBelt: { en: 'Seat Belt', ar: 'أحزمة الأمان' },
      seatOptions: { en: 'Seat Options', ar: 'خيارات المقاعد' },
      stitch: { en: 'Stitch', ar: 'الخياطة' },
},
    conflictDialog: {
      description: { en: 'In order to apply this, the following item(s) will need to be removed due to compatibility constraints. Please review the item(s) below and proceed with your change.', ar: 'لتطبيق هذا التغيير، يجب إزالة العناصر التالية بسبب قيود التوافق. يُرجى مراجعتها أدناه ثم متابعة التغيير.' },
    },
    viewport: {
      changeBackground: { en: 'Change Background', ar: 'تغيير الخلفية' },
      selectLanguage: { en: 'Switch Language (عربي)', ar: 'تبديل اللغة (English)' },
      menu: { en: 'Menu', ar: 'القائمة' },
      exploreFeatures: { en: 'Explore Features', ar: 'استكشف الميزات' },
          applyingColor: { en: 'Applying color…', ar: 'جارٍ تطبيق اللون…' },
},
      remove: { en: 'Remove', ar: 'إزالة' },
    localMatchesNudge: {
      message: {
        en: 'We Found a few local matches similar to your current configuration. Click to view Matches',
        ar: 'لقد وجدنا بعض المركبات المتوفرة محلياً والمشابهة لتخصيصك الحالي. انقر لعرض المركبات المطابقة.',
      },
    },
},
  dealers: {
    UAE: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
    KSA: [
      {
        id: 0,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307261',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Aziziyah Madina Road, Jeddah',
          ar: 'الجميح للسيارات - العزيزية طريق المدينة، جدة',
        },
      },
      {
        id: 1,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164034',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Hail City King Abdullah Road. Hail',
          ar: 'الجميح للسيارات - مدينة حائل طريق الملك عبدالله. يشيد',
        },
      },
      {
        id: 2,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164035',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Alshlhoub King Abdullah Road, Al Jouf',
          ar: 'الجميح للسيارات - الشلهوب طريق الملك عبدالله، الجوف',
        },
      },
      {
        id: 3,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307271',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Qassim King Abdulaziz Road, Buraidah',
          ar: 'الجميح للسيارات - القصيم طريق الملك عبد العزيز، بريدة',
        },
      },
      {
        id: 4,
        country: 'KSA',
        value: 'KSA',
        dealerId: '260921',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Jededah Road, Arar',
          ar: 'الجميح للسيارات - طريق الجديدة، عرعر',
        },
      },
    ],
    KW: [
      {
        id: 0,
        country: 'KW',
        value: 'KW',
        dealerId: '122620',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Al Rai 4th Ring Road, Al Rai',
          ar: 'الامانة للسيارات - الري الطريق الدائري الرابع، الري',
        },
      },
      {
        id: 1,
        country: 'KW',
        value: 'KW',
        dealerId: '174979',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Fahaheel Industrial Area Dabbous Street, Fahaheel',
          ar: 'الامانة للسيارات - الفحيحيل الصناعية شارع الدبوس، الفحيحيل',
        },
      },
    ],
    BHD: [
      {
        id: 0,
        country: 'BHD',
        value: 'BHD',
        dealerId: '122215',
        dealerName: 'National Motor Company W.L.L',
        label: {
          en: 'National Motor Company W.L.L, Al Qarya 475 Shk. Jaber Al Subah Hwy',
          ar: 'الشركة الوطنية للسيارات ذ.م.م، القرية 475 شارع الشيخ. طريق جابر الصباح',
        },
      },
    ],
    IQD: [
      {
        id: 0,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295876',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Al-Karada Sadoun Street',
          ar: 'شركة ماك الدولية (منطقة حرة)، شارع الكرادة سعدون',
        },
      },
      {
        id: 1,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295879',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Kurdistan District 100 m Street',
          ar: 'ماك إنترناشونال إف زي إي، منطقة كردستان، شارع ١٠٠ متر',
        },
      },
    ],
    LBP: [
      {
        id: 0,
        country: 'LBP',
        value: 'LBP',
        dealerId: '122226',
        dealerName: 'IMPEX',
        label: {
          en: 'IMPEX -  Badaro, Near the National Museum , Beirut',
          ar: 'إمبكس - بدارو، بالقرب من المتحف الوطني، بيروت',
        },
      },
    ],
    QAR: [
      {
        id: 0,
        country: 'QAR',
        value: 'QAR',
        dealerId: '274766',
        dealerName: 'Jaidah',
        label: {
          en: 'Jaidah Square Showroom - Al Matar Street Umm Ghuwallina, Doha',
          ar: 'صالة عرض "جايدة سكوير" - شارع المطار، أم غويلينة، الدوحة',
        },
      },
    ],
    OMR: [
      {
        id: 0,
        country: 'OMR',
        value: 'OMR',
        dealerId: '122219',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Ghalla Al Maardh Street',
          ar: 'مؤسسة عمان التجارية ش.م.م، شارع غلا المعرض',
        },
      },
      {
        id: 1,
        country: 'OMR',
        value: 'OMR',
        dealerId: '234217',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Al Farq Street',
          ar: 'مؤسسة عمان التجارية ذ.م.م، شارع الفرق',
        },
      },
      {
        id: 2,
        country: 'OMR',
        value: 'OMR',
        dealerId: '260896',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Hail Al Shumaliah Street',
          ar: 'مؤسسة عمان التجارية ذ.م.م، شارع حيل الشمالية',
        },
      },
    ],
    JOD: [
      {
        id: 0,
        country: 'JOD',
        value: 'JOD',
        dealerId: '189129',
        dealerName: 'Leading Vehicles',
        label: {
          en: 'The Leading Vehicles Co. Ltd, Al Rawabi',
          ar: 'شركة المركبات الرائدة المحدودة، الروابي',
        },
      },
    ],
    default: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
  },
  featureFlags: {
    showPriceInPresentation: false,
    showLogo: true,
    showSpecDetailsButton: true,
    useChevroletFont: true,
    showLocalMatches: true,
    showDisclaimerInPresentation: true,
    // Master toggle for per-item pricing: price lines on variant / colour /
    // accessory / package cards, the MODEL trim price, the SUMMARY tab's per-line
    // prices and total, and whether paint / interior trim count toward the
    // headline total. Off by default for every other project — see
    // `arePricesVisible` in src/utils/pricing.ts. The header "Starting Price"
    // is NOT gated by this; every project has always shown it.
    showPrices: true,
    showDisplayCarPrice: true, // Toggle to show car price in CarDisplay UI
    showCart: false, // Toggle to show/hide cart button
  },
  // Set mobile/desktop to false to hide that tab on the respective screen size
  // toggleTabs is now variant-specific — see each variant's toggleTabs field above
  config: {
    externalAnalytics: {
      brand: 'chevrolet',
      provider: 'adobe',
      modelName: 'corvette',
    },
    // Lead-side model name — 1.0's `config.model`, which is a lead label
    // distinct from 2.0's `config.model` (the models[] key).
    leadModel: 'corvette',
    // Dial code (prefixed onto mobile_number) and Salesforce lead country
    // code (metadome_country_code), keyed by the `country` URL parameter.
    countryCodes: {
      UAE: {
        code: '+971',
        leadCode: 'AE',
      },
      KSA: {
        code: '+966',
        leadCode: 'SA',
      },
      KW: {
        code: '+965',
        leadCode: 'KW',
      },
      BHD: {
        code: '+973',
        leadCode: 'BH',
      },
      IQD: {
        code: '+964',
        leadCode: 'IQ',
      },
      LBP: {
        code: '+961',
        leadCode: 'LB',
      },
      OMR: {
        code: '+968',
        leadCode: 'OM',
      },
      QAR: {
        code: '+974',
        leadCode: 'QA',
      },
      JOD: {
        code: '+962',
        leadCode: 'JO',
      },
      default: {
        code: '+971',
        leadCode: 'AE',
      },
    },
    // Display currency + USD conversion factor per region — see `_regions`.
    regions: _regions,
    model: 'Corvette',
    modelName: 'Corvette',
    pageTitle: 'Corvette Configurator',
    // Corvette assets are published only on the alpha release channel (no
    // beta/gamma yet). Pin the channel per-project so it resolves in every
    // environment (local + deployed) regardless of build env vars. Update to
    // 'gammaVersion' once the assets are promoted to gamma.
    releaseChannel: 'alphaVersion',
    client: 'GM',
    brand: 'chevrolet',
    modelId: 'CHTAHMCM_2025',
    // modelYear: 2025,
    configurationId: 'l25zrwex1u93',
    presentation: {
      video: 'images/2387/presentation_mode.webm',
      videoMobile: 'images/2387/phone_presentation_mode.webm',
      loadingImage: 'images/2387/desktop_slider.png',
      transitionImage: 'images/2387/transition_logo.png',
      // Chevrolet header logo is full-color — keep it as-is in dark mode (no invert).
      headerLogoKeepColor: true,
      keyHighlights: [
        {
          index: 0,
          show: true,
          title: {
            en: '',
            ar: '',
          },
          value: {
            en: '535 HP',
            ar: '535 حصان',
          },
          unit: '',
        },
        {
          index: 1,
          show: true,
          title: {
            en: 'Torque',
            ar: 'عزم الدوران',
          },
          value: {
            en: '705 NM',
            ar: '705 نيوتن متر',
          },
          unit: '',
        },
        {
          index: 2,
          show: true,
          title: {
            en: 'Turbo Engine',
            ar: 'محرك توربو',
          },
          value: {
            en: 'V8',
            ar: 'V8',
          },
          unit: '',
        },
      ],
    },
  },
  saveFormConfig: {
    image: 'images/2387/Save.png',
    fields: [
      {
        id: 'firstName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelFirstName',
        placeholderKey: 'saveDialog.placeholderFirstName',
      },
      {
        id: 'lastName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelLastName',
        placeholderKey: 'saveDialog.placeholderLastName',
      },
      {
        id: 'email',
        type: 'email',
        required: true,
        labelKey: 'saveDialog.labelEmail',
        placeholderKey: 'saveDialog.placeholderEmail',
        visibility: ['email', 'test-drive', 'callback'],
      },
      {
        id: 'phone',
        type: 'phone',
        required: true,
        labelKey: 'saveDialog.labelMobileNumber',
        placeholderKey: 'saveDialog.placeholderMobileNumber',
        visibility: ['whatsapp', 'test-drive', 'callback'],
      },
      {
        id: 'model',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelModel',
        placeholderKey: 'saveDialog.labelModel',
        optionsSource: 'models',
        visibility: ['test-drive', 'callback'],
      },
      {
        id: 'dealer',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelDealership',
        placeholderKey: 'saveDialog.placeholderSelectDealership',
        optionsSource: 'dealers',
        visibility: [
          'test-drive',
          'callback',
          'whatsapp',
          'email',
        ],
        // 1.0's save forms show the dealership dropdown but do not gate
        // submission on it; the booking forms and Save to WhatsApp
        // require it (GM needs a dealer on WhatsApp leads); Email stays optional.
        requiredFor: [
          'test-drive',
          'callback',
          'whatsapp',
        ],
      },
      {
        id: 'testDriveDate',
        type: 'date',
        required: true,
        labelKey: 'saveDialog.labelTestDriveDate',
        placeholderKey: 'saveDialog.placeholderTestDriveDate',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'testDriveTime',
        type: 'time',
        required: true,
        labelKey: 'saveDialog.labelTestDriveTime',
        placeholderKey: 'saveDialog.placeholderTestDriveTime',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'comments',
        type: 'textarea',
        required: false,
        labelKey: 'saveDialog.labelComments',
        placeholderKey: 'saveDialog.placeholderComments',
        visibility: ['test-drive', 'callback'],
      },
    ],
  },
  externalScript: {
    dev: {
      head: {
        links: [
          {
            snippet:
              'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-bea0833819b4-development.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet:
              'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: `<!-- Meta Pixel Code -->
<script>
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(argum
ents)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window,
document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '2090290248171811');
fbq('track', 'PageView');
</script>
<noscript><img height="1" width="1"
style="display:none"
src="https://www.facebook.com/tr?
id=2090290248171811&ev=PageView&noscript=1"
/></noscript>
<!-- End Meta Pixel Code -->
        `,
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    staging: {
      head: {
        links: [
          {
            snippet:
              'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-77110a86d4ec-staging.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet:
              'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: `<!-- Meta Pixel Code -->
<script>
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(argum
ents)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window,
document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '2090290248171811');
fbq('track', 'PageView');
</script>
<noscript><img height="1" width="1"
style="display:none"
src="https://www.facebook.com/tr?
id=2090290248171811&ev=PageView&noscript=1"
/></noscript>
<!-- End Meta Pixel Code -->
        `,
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    main: {
      head: {
        links: [
          {
            snippet:
              'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-85b05c76e071.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet:
              'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: `<!-- Meta Pixel Code -->
<script>
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(argum
ents)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window,
document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '2090290248171811');
fbq('track', 'PageView');
</script>
<noscript><img height="1" width="1"
style="display:none"
src="https://www.facebook.com/tr?
id=2090290248171811&ev=PageView&noscript=1"
/></noscript>
<!-- End Meta Pixel Code -->
        `,
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
  },
  filterOptions: [
    {
      id: 'drivetrain',
      label: { en: 'Drivetrain', ar: 'مجموعة نقل الحركة' },
      disabled: true,
      type: 'chip' as const,
      key: 'description',
      options: [
        {
          id: 'awd',
          label: { en: 'AWD', ar: 'دفع رباعي' },
          value: 'AWD',
          checked: false,
          subtitle: { en: 'All-Wheel Drive', ar: 'الدفع الرباعي' },
          description: { en: 'Superior traction and handling in all conditions', ar: 'تماسك وتحكم فائقان في جميع الظروف' },
        },
        {
          id: 'fwd',
          label: { en: 'FWD', ar: 'الدفع الأمامي' },
          value: 'FWD',
          checked: false,
          subtitle: { en: 'Front-Wheel Drive', ar: 'الدفع الأمامي' },
          description: { en: 'Efficient everyday performance', ar: 'أداء يومي فعال' },
        },
      ],
    },
    {
      id: 'trim',
      label: { en: 'Trim', ar: 'الفئة' },
      disabled: true,
      type: 'chip' as const,
      key: 'name',
      options: [
        {
          id: 'z71',
          label: { en: 'Z71', ar: 'Z71' },
          value: 'Z71',
          checked: false,
          subtitle: { en: 'Off-Road Ready', ar: 'جاهزة للطرق الوعرة' },
          description: { en: 'Advanced Twin-Clutch AWD with off-road capability', ar: 'نظام دفع رباعي متقدم بتعشيق مزدوج، مع قدرات للطرق الوعرة' },
        },
        {
          id: 'lt',
          label: { en: 'LT', ar: 'LT' },
          value: 'LT',
          checked: false,
          subtitle: { en: 'Premium Comfort', ar: 'راحة فائقة' },
          description: { en: 'Available in AWD and FWD configurations', ar: 'متوفر بنظامَي دفع رباعي وأمامي' },
        },
      ],
    },
  ],
  // Brand theme tokens for Chevrolet Corvette.
  // Colors are the existing Chevrolet blue (#0077D9) used throughout the UI.
  // Explicitly declared here so the brand theming is self-documenting; these
  // match the :root defaults in index.css and can be overridden per-project.
  styleConfig: {
    cssVariables: {
      '--default-font-family': 'Chevy Sans',
      '--bold-font-family': 'Chevy Sans Medium',
      '--brand-primary': '#0077D9',
      '--brand-primary-hover': '#0062BA',
      '--brand-secondary': '#0077D9',
      '--brand-accent': '#0077D9',
      '--brand-accent-hover': '#0066c0',
      '--brand-accent-light': '#dee5f2',
      '--brand-surface': '#f2f2f2',
      '--brand-surface-alt': '#f3f4f6',
      '--brand-text': '#000000',
      '--brand-text-secondary': '#666666',
      '--brand-text-tertiary': '#bebebe',
      '--brand-border': '#e5e7eb',
      '--brand-border-selected': '#0077D9',
      '--brand-card-bg': '#ffffff',
      '--brand-toggle-gradient-from': '#0077D9',
      '--brand-toggle-gradient-to': '#0077D9',
      '--brand-toggle-knob-border': '#09f',
      '--brand-accent-secondary': '#EB212B',
      // Chevrolet uses rounded corners per existing design
      '--brand-card-radius': '10px',
      '--brand-card-radius-sm': '7px',
      '--brand-card-radius-lg': '12px',
      '--brand-button-radius': '6px',
      '--brand-button-radius-sm': '5px',
      '--brand-pill-radius': '50px',
      '--brand-cta-radius': '8px',
      '--brand-button-border': '#0077D9',
      '--brand-dialog-radius': '11px',
      '--brand-dialog-option-radius': '8px',
    },
  },
} as unknown as UIMetadataComplete;

// ── Accessory thumbnails (public/assets/images/2387/<Variant Folder>/...) ────
// Alloys / Graphics / Seat belt / Stitch subfolder files are named by interaction
// (derived from the graphics accessory's own message). Variant-root files are named
// by description (mapped explicitly). Accessories are shared across variants, so a
// canonical folder is picked per group. Missing files fall back to the placeholder.
(() => {
  const R = 'images/2387';
  const uAcc = (uimetadata as any).models?.Corvette?.accessories as Record<string, any> | undefined;
  const gAcc = (_graphics as any).models?.Corvette?.accessories as Record<string, any> | undefined;
  if (!uAcc || !gAcc) return;
  const set = (id: string, path: string) => { if (uAcc[id]) uAcc[id].image = path; };

  // Interaction-named subfolders — file = accessory interaction message.
  for (const id of Object.keys(gAcc)) {
    const a = gAcc[id];
    const msg: string = a.applyMessages?.[0]?.message ?? '';
    if (a.groupId === 'SeatBelt') {
      const folder = msg.endsWith('_GT1') ? 'Stingray Coupe 1LT' : 'Stingray Coupe 3LT';
      set(id, `${R}/${folder}/Seat belt/${msg}.jpg`);
    } else if (a.groupId === 'Stitch') {
      const file: string = a.applyMessages?.[1]?.message ?? msg; // seat-specific, e.g. Stitch_Blue_GT2
      if (!/White/.test(file)) set(id, `${R}/Stingray Coupe 3LT/Stitch/${file}.jpg`);
    } else if (a.groupId === 'Alloy Wheels') {
      if (msg !== 'ROU') {
        const folder = id.startsWith('gsAlloy') ? 'GS X Coupe 3LZ' : 'Stingray Coupe 3LT';
        set(id, `${R}/${folder}/Alloys/${msg}.jpg`);
      }
    } else if (a.groupId === 'Graphics') {
      const folder = id.startsWith('gsGraphics')
        ? 'GS X Coupe 3LZ'
        : id.startsWith('convGraphics')
          // GS convertible graphics carry "_GS" somewhere in the message (full-length end
          // in "_Conv_GS", heritage in "_GS_Convertible"); Stingray convertible use "_Stin".
          ? (/_GS/.test(msg) ? 'GS X Convertible 3LZ' : 'Stingray Convertible 2LT')
          : 'Stingray Coupe 3LT';
      set(id, `${R}/${folder}/Graphics/${msg}.jpg`);
    }
  }

  // Description-named variant-root files.
  const CV = 'Stingray Coupe 3LT', GS = 'GS X Coupe 3LZ', CC = 'Stingray Convertible 2LT', GC = 'GS X Convertible 3LZ';
  const desc: Record<string, string> = {
    accCaliperVelocityYellow: `${CV}/Velocity Yellow-painted brake calipers.jpg`,
    accCaliperBrightRed: `${CV}/Bright Red-painted brake calipers.jpg`,
    accCaliperEdgeRed: `${CV}/Edge Red-painted brake calipers.jpg`,
    accCaliperBlue: `${CV}/Blue-painted brake calipers.jpg`,
    gsCaliperBrightRed: `${GS}/Bright Red-painted brake calipers.jpg`,
    gsCaliperEdgeRed: `${GS}/Edge Red-painted brake calipers.jpg`,
    gsCaliperVelocityYellow: `${GS}/Velocity Yellow-painted brake calipers.jpg`,
    gsCaliperOrange: `${GS}/Orange-painted brake calipers.jpg`,
    accRoofCarbonFiber: `${CV}/Visible carbon-fiber roof panel with body-color surround.jpg`,
    convRoofCarbonFlash: `${CC}/Carbon Flash-painted nacelles and roof.jpg`,
    convRoofCarbonFlashBody: `${CC}/Carbon Flash-painted nacelles and body-color roof.jpg`,
    accEngineRed: `${CV}/6.2L engine cover in Edge Red.jpg`,
    accEngineSilver: `${CV}/6.2L engine cover in Sterling Silver.jpg`,
    accEngineBlue: `${CV}/6.2L engine cover in Blue.jpg`,
    accQuadExhaustBlack: `${CV}/Quad center exit exhaust.jpg`,
    accQuadExhaustBright: `${CV}/Bright center quad exposed exhaust tips.jpg`,
    accPkgStealthTrim: `${CV}/Stealth Interior Trim Package.jpg`,
    accPkgCarbonFiberTrim: `${CV}/Carbon Fiber Interior Trim Package.jpg`,
    accPkgEngineAppearance: `${CV}/Coupe Engine Appearance Package.jpg`,
    accBodyColorAccent: `${CV}/Body-color exterior accents.jpg`,
    accSpoilerLowProfile: `${CV}/Low-profile rear spoiler and front splitter.jpg`,
    accSpoilerAeroDelete2lt: `${CV}/Aero Delete (Z51 Performance Package).jpg`,
    convPkgStealthTrim: `${CC}/Stealth Interior Trim Package.jpg`,
    convPkgCarbonFiberTrim: `${CC}/Carbon Fiber Interior Trim Package.jpg`,
    gsBodyColorAccent: `${GS}/Body-color exterior accents.jpg`,
    gsGroundEffectsCarbonFiber: `${GS}/Visible carbon fiber ground effects.jpg`,
    gsGroundEffectsCarbonFlash: `${GS}/Carbon Flash-painted carbon fiber ground effects.jpg`,
    accBadgeChrome: `${CV}/Chrome Exterior Badge Package.jpg`,
    gsBadgeChromeCoupe: `${GS}/Chrome Exterior Badge Package.jpg`,
    gsBadgeChromeConv: `${GC}/Chrome Exterior Badge Package.jpg`,
    convBadgeChromeStingray: `${CC}/Chrome Exterior Badge Package.jpg`,
  };
  for (const id of Object.keys(desc)) set(id, `${R}/${desc[id]}`);

  // Filename quirk: this thumbnail ships with a stray space before "_GS".
  set('gsGraphics_6795', `${R}/GS X Coupe 3LZ/Graphics/Carbon_Flash_Blue_Accent_Coupe _GS.jpg`);

  // Competition Sport seats share one thumbnail (root of the 2387 folder). GT2 is the
  // hidden default, so only the sport alternatives need an image.
  for (const id of Object.keys(uAcc)) {
    if (/^seat_.*_sport(_sueded)?$/.test(id)) set(id, `${R}/Competition Sport seats.jpg`);
  }
  // Carbon Flash Metallic mirror + Engine Appearance thumbnails.
  set('accOrvmCarbonFlash', `${R}/${CV}/Carbon Flash Metallic-painted outside mirrors and spoiler (when equipped).jpg`);
  set('gsCarbonFlashPkg', `${R}/${GS}/Carbon Flash Metallic-painted outside mirrors and spoiler (when equipped).jpg`);
  set('convPkgEngineAppearance', `${R}/Engine Appearance Package.jpg`);

  // ── Feature (cinematic) thumbnails — <Variant>/Cinematics/<name>.jpg ───────
  // Features are shared, so a canonical folder that ships the file is used per feature.
  const uFeat = (uimetadata as any).models?.Corvette?.features as Record<string, any> | undefined;
  if (uFeat) {
    const C = `${CV}/Cinematics`, C1 = 'Stingray Coupe 1LT/Cinematics';
    const featImg: Record<string, string> = {
      featRemovableRoof: `${C}/Removable Roof.jpg`,
      featFrontSuspension: `${C}/Front lift suspension Ground Clearance.jpg`,
      featExhaust: `${C1}/Performance exhaust.jpg`,
      featExteriorDesign: `${C}/Exterior design.jpg`,
      featWideBody: `${C}/Wide-body aerodynamics.jpg`,
      featHeadlamps: `${C}/Automatic leveling headlamps.jpg`,
      featSpeaker: `${C}/14 -speaker.jpg`,
      featHeatedSeats: `${C}/Heated and ventilated seat.jpg`,
      featHUD: `${C}/Head -UpDisplay.jpg`,
      featSteeringWheel: `${C}/Heated steering wheel.jpg`,
      featGT2Seats: `${C}/Standard GT2 seats with Napa leather seating.jpg`,
      featInstrumentPanel: `${C}/Custom Leather -wrapped instrument panel.jpg`,
      featSuededTrim: `${C}/Custom Leather -wrapped instrument panel.jpg`,
      featInstrumentPanelDoors: `${C}/Custom Leather -wrapped instrument panel.jpg`,
    };
    for (const id of Object.keys(featImg)) if (uFeat[id]) uFeat[id].image = `${R}/${featImg[id]}`;
    // Feature-card RPO codes from the 2027 sheet. featExteriorDesign / featWideBody
    // and the leather-wrapped instrument-panel cards are descriptive showcases with
    // no orderable option on the sheet, so they stay uncoded.
    const featRPO: Record<string, string> = {
      featHUD: 'UV6',              // Head-Up Display
      featExhaust: 'NPP',          // Exhaust, performance
      featSteeringWheel: 'KI3',    // Steering wheel, heated
      featSuededTrim: 'IWE',       // Sueded Microfiber-Wrapped Upper Interior Trim Package
      featHeatedSeats: 'KQV',      // Seats, heated and vented driver and passenger
      featGT2Seats: 'AH2',         // Seats, GT2 bucket
      featRemovableRoof: 'CF7',    // Roof panel, body-color, removable
      featFrontSuspension: 'E60',  // Front lift adjustable height
      featHeadlamps: 'TR7',        // Headlamp control, automatic leveling system
      featSpeaker: 'UQH',          // Bose Performance Series Sound System (14 speakers)
    };
    for (const id of Object.keys(featRPO)) if (uFeat[id]) uFeat[id].autoIntelRPOCode = featRPO[id];
  }

  // ── AutoIntel RPO codes (2027 Corvette RPO sheet) ─────────────────────────
  // The sheet covers accessories / packages / wheels / seats / belts / stitch. Base
  // exterior/interior paint colours and trim levels are NOT in it, so they stay uncoded.
  const RPO: Record<string, string> = {
    // Calipers (the black / Dark Gray Metallic defaults are hidden base states, so uncoded)
    accCaliperBlue: 'J6B', accCaliperEdgeRed: 'J6N', accCaliperBrightRed: 'J6F', accCaliperVelocityYellow: 'J6E',
    gsCaliperOrange: 'J6L', gsCaliperBrightRed: 'J6F', gsCaliperEdgeRed: 'J6N', gsCaliperVelocityYellow: 'J6E',
    // Engine cover
    accEngineRed: 'BCP', accEngineSilver: 'BCS', accEngineBlue: 'BC4',
    // Roof
    accRoofCarbonFiber: 'C2Z', convRoofCarbonFlash: 'D84', convRoofCarbonFlashBody: 'D86',
    // Exhaust: Quad center-exit (Performance_engine default + Quad Black/Bright). No RPO
    // codes provided yet for accQuadExhaustBlack / accQuadExhaustBright.
    // Mirrors / spoiler (Carbon Flash Metallic mirrors and spoiler). accOrvmBodyColor
    // (body-color mirror default) has no RPO code in the sheet, so it's left uncoded.
    accOrvmCarbonFlash: 'ZYC', gsCarbonFlashPkg: 'ZYC',
    // Ground effects (GSX factory options, not the LPO variants)
    gsGroundEffectsCarbonFiber: 'CFV', gsGroundEffectsCarbonFlash: 'CFZ',
    // GS X full-length dual racing stripes / stinger stripes (coupe gsGraphics_*, conv convGraphics_*)
    gsGraphics_6795: 'DPB', gsGraphics_6707: 'DPC', gsGraphics_6785: 'DPG', gsGraphics_6717: 'DPL', gsGraphics_6695: 'DPT',
    gsGraphics_6703: 'DTH', gsGraphics_6721: 'DSY', gsGraphics_6787: 'DSZ', gsGraphics_6786: 'DUB', gsGraphics_6723: 'DT0',
    gsGraphics_6714: 'DZX', gsGraphics_6747: 'DZU', gsGraphics_6741: 'DZV', gsGraphics_6698: 'DUK',
    convGraphics_6808: 'DPB', convGraphics_6805: 'DPC', convGraphics_6806: 'DPG', convGraphics_6811: 'DPL', convGraphics_6816: 'DPT',
    convGraphics_6818: 'DTH', convGraphics_6819: 'DSY', convGraphics_6809: 'DSZ', convGraphics_6817: 'DUB', convGraphics_6820: 'DT0', convGraphics_6812: 'DUK',
    // Stingray full-length dual racing / stinger stripes (graphics_*)
    graphics_6843: 'DPB', graphics_6842: 'DPC', graphics_6847: 'DPG', graphics_6832: 'DPL', graphics_6838: 'DPT',
    graphics_6824: 'DTH', graphics_6839: 'DSY', graphics_6835: 'DSZ', graphics_6845: 'DUB', graphics_6827: 'DT0',
    graphics_6823: 'DZX', graphics_6831: 'DZU', graphics_6829: 'DZV',
    // Asymmetrical stripes matched by colour position: red -> DUK (sheet "Edge Red"),
    // blue -> DUE (sheet "Royal Blue"). Our names say Adrenaline Red/Blue.
    graphics_6821: 'DUK', graphics_6857: 'DUE',
    // Exterior badge package
    accBadgeChrome: 'EYK', gsBadgeChromeCoupe: 'EYK', gsBadgeChromeConv: 'EYK', convBadgeChromeStingray: 'EYK',
    // Engine appearance package
    accPkgEngineAppearance: 'B6P', convPkgEngineAppearance: 'ZZ3',
    // Interior trim packages (no distinct Carbon Fiber Interior Trim code in the sheet)
    accPkgStealthTrim: 'BAZ', convPkgStealthTrim: 'BAZ',
    // Spoiler / accents.
    accSpoilerLowProfile: 'TVS',
    accBodyColorAccent: 'EFY', gsBodyColorAccent: 'EFY',
    // GS X alloys
    gsAlloyROU: 'ROU', gsAlloyROX: 'ROX', gsAlloySOM: 'SOM', gsAlloySON: 'SON', gsAlloyROY: 'ROY', gsAlloyROZ: 'ROZ', gsAlloySTZ: 'STZ',
    // ── From the 2027 Corvette RPO_CODES sheet ("All" tab) ──────────────────
    // Grand Sport Heritage Hash Marks (coupe gsGraphics_* + convertible convGraphics_*)
    gsGraphics_6991: '55A', gsGraphics_6996: '17A', gsGraphics_7002: 'DX4',
    gsGraphics_7009: '97A', gsGraphics_7011: '75A', gsGraphics_7013: '20A',
    convGraphics_7015: '55A', convGraphics_7017: '17A', convGraphics_7014: 'DX4',
    convGraphics_7019: '97A', convGraphics_7018: '75A', convGraphics_7016: '20A',
    // GS X asymmetrical Royal Blue stripe (sheet: "Asymmetrical Royal Blue/Carbon Flash")
    gsGraphics_7003: 'DUE',
    // Convertible full-length dual racing / stinger stripes — same options as the
    // coupe, matched 1:1 on the sheet description.
    convGraphics_6822: 'DSZ', convGraphics_6823: 'DZX', convGraphics_6825: 'DT0',
    convGraphics_6826: 'DPT', convGraphics_6828: 'DTH', convGraphics_6829: 'DZV',
    convGraphics_6831: 'DZU', convGraphics_6833: 'DPG', convGraphics_6834: 'DPB',
    convGraphics_6836: 'DUB', convGraphics_6840: 'DPC', convGraphics_6841: 'DUK',
    convGraphics_6844: 'DPL', convGraphics_6848: 'DSY',
    convGraphics_6867: 'DUE', convGraphics_6995: 'DUE',
    // Exhaust (both new for 2027)
    accQuadExhaustBlack: 'WUB',  // sheet: "Exhaust, quad center exit"
    accQuadExhaustBright: 'NWI', // sheet: "Exhaust tips, bright"
    // Steering wheels
    steering_default: 'NK4',     // sheet: "Steering wheel, leather-wrapped" (standard)
    steering_sueded: 'N26',      // sheet: "Steering wheel, sueded microfiber-wrapped"
    // Aero delete (Z51) — sheet has a dedicated code
    accSpoilerAeroDelete2lt: 'ZF1',
    // Interior trim (sheet lists FA5/FA6 as "Trim, interior"; FA5 is the Carbon
    // Fiber Interior Trim Package per the configurator's own swatch URL)
    accPkgCarbonFiberTrim: 'FA5', convPkgCarbonFiberTrim: 'FA5',
  };
  for (const id of Object.keys(RPO)) if (uAcc[id]) uAcc[id].autoIntelRPOCode = RPO[id];

  // Seats / seat belts / stitch — coded by pattern (helper-generated ids).
  // Black belt is the standard/default belt but the 2027 sheet does list it as 719,
  // so it is mapped like the rest. White stitch has no code in the sheet (standard).
  const beltCode: Record<string, string> = { black: '719', orange: '379', santorini: '3A9', blue: '3A9', torch: '3F9', red: '3F9', yellow: '3M9', natural: '3N9' };
  const stitchCode: Record<string, string> = { yellow: '36S', santorini: '37S', blue: '37S', adrenaline: '38S', red: '38S' };
  for (const id of Object.keys(uAcc)) {
    const lc = id.toLowerCase();
    if (!uAcc[id].autoIntelRPOCode) {
      if (/_sport(_sueded)?$/.test(id)) uAcc[id].autoIntelRPOCode = 'AE4';       // Competition Sport bucket
      else if (/_gt2(_sueded)?$/.test(id)) uAcc[id].autoIntelRPOCode = 'AH2';    // GT2 bucket
    }
    if (id.startsWith('seatbelt_')) {
      const k = Object.keys(beltCode).find((c) => lc.includes(c));
      if (k) uAcc[id].autoIntelRPOCode = beltCode[k];
    } else if (id.startsWith('stitch_')) {
      const k = Object.keys(stitchCode).find((c) => lc.includes(c));
      if (k) uAcc[id].autoIntelRPOCode = stitchCode[k];
    }
  }

  // ── Color swatches ──────────────────────────────────────────────────────
  // Real swatch photos (scraped from chevrolet.com's live configurator, small_byo3
  // CDN, one file per GM RPO code) now live under images/2387/rpo/<CODE>.jpg — see
  // RPO_CODE_HAS_IMAGE below for exactly which codes shipped. `hex` stays as a
  // fallback/accent color for anything not covered (kept from the original
  // hex-only design, which predates these downloads).
  const RPO_CODE_HAS_IMAGE = new Set([
    '36S', '379', '37S', '38S', '3A9', '3F9', '3M9', '3N9', '5DG', '5DO', '5JR', '5V7', '5ZC', '5ZD',
    '5ZU', '5ZW', '5ZZ', '719', 'AE4', 'AH2', 'AQ9', 'AUP', 'B6P', 'BAZ', 'BC7', 'BCP', 'BCS', 'BV4',
    'C2Z', 'CAV', 'CC3', 'CFX', 'D3V', 'D84', 'D86', 'DPB', 'DPC', 'DPG', 'DPL', 'DPT', 'DSY', 'DSZ',
    'DT0', 'DTG', 'DTH', 'DTK', 'DUB', 'DUK', 'DUW', 'DZU', 'DZV', 'DZX', 'E60', 'EFY', 'EJH', 'EMT',
    'EPX', 'ERI', 'EYK', 'FA5', 'FE2', 'FE3', 'FE4', 'G26', 'G4Z', 'G8G', 'GBA', 'GBK', 'GJV', 'GKA',
    'GKZ', 'GPH', 'GXL', 'H8T', 'HMO', 'HNK', 'HTA', 'HTE', 'HTG', 'HTJ', 'HTT', 'HU0', 'HU1', 'HU2',
    'HU9', 'HUA', 'HUB', 'HUC', 'HUE', 'HUF', 'HUP', 'HUQ', 'HUU', 'HUW', 'HUX', 'HVT', 'HVV', 'HVZ',
    'HXO', 'HZB', 'HZN', 'HZP', 'J55', 'J6E', 'J6F', 'J6N', 'M1L', 'N26', 'NGA', 'NPP', 'PCU', 'PCX',
    'PDV', 'PDY', 'PEF', 'PRB', 'Q99', 'Q9A', 'Q9I', 'Q9O', 'Q9Y', 'QE5', 'QEB', 'QUD', 'R6P', 'R8C',
    'R9V', 'R9Y', 'RIA', 'RIK', 'RIN', 'RNX', 'RWH', 'RWJ', 'RWU', 'RXH', 'RXJ', 'RYQ', 'RZ9', 'S2L',
    'S47', 'SB7', 'SC7', 'SFE', 'SFZ', 'SHQ', 'SHT', 'SHW', 'SL1', 'SL8', 'SL9', 'SLK', 'SLN', 'SNG',
    'SPY', 'SPZ', 'STI', 'SXB', 'SXR', 'SXT', 'TU7', 'TVS', 'UQT', 'USA', 'V8X', 'VQK', 'VTB', 'VUP',
    'VWD', 'VWE', 'VYW', 'W2D', 'WKQ', 'WKR', 'Z51', 'ZYC', 'ZZ3',
  ]);
  const gExt = (_graphics as any).models?.Corvette?.colors?.exterior ?? {};
  const uExt = (uimetadata as any).models?.Corvette?.colors?.exterior ?? {};
  const EXT_HEX: Record<string, string> = {
    '6666': '#FFFFFF', '6701': '#000000', '6662': '#A60100', '6669': '#F7C815',
    '6716': '#D84B16', '6724': '#1D4D3A', '6739': '#C0C4C7', '6645': '#8D0A13',
    '6997': '#3C3F42', '6990': '#22314F',
  };
  for (const id of Object.keys(uExt)) {
    const sku = gExt[id]?.sku;
    if (sku && EXT_HEX[sku]) uExt[id].hex = EXT_HEX[sku];
    const code = uExt[id]?.autoIntelRPOCode;
    if (code && RPO_CODE_HAS_IMAGE.has(code)) { uExt[id].image = `${R}/rpo/${code}.jpg`; uExt[id].cartImage = `${R}/rpo/${code}.jpg`; }
  }
  const uInt = (uimetadata as any).models?.Corvette?.colors?.interior ?? {};
  const INT_HEX: Array<[RegExp, string]> = [
    [/UltimateSuede/i, '#1c1c1c'],
    [/JetBlack/i, '#181818'],
    [/SkyCoolGray/i, '#8b9096'],
    [/NaturalDipped/i, '#a07d55'],
    [/Natural/i, '#c2a079'],
    [/AdrenalineRedDipped/i, '#5e1212'],
    [/AsymmetricalAdrRed/i, '#4a1414'],
    [/AdrenalineRed/i, '#8f1c1c'],
    [/SantoriniBlue/i, '#2e4a6b'],
    [/Habanero/i, '#b5471f'],
    [/VeryDark/i, '#262626'],
  ];
  for (const id of Object.keys(uInt)) {
    const match = INT_HEX.find(([re]) => re.test(id));
    if (match) uInt[id].hex = match[1];
  }
  // Interior colour photos, resolved via the same per-variant RPO maps used for
  // AutoIntel (_interiorColorRPO) — a color id's real photo differs by which model's
  // code we actually downloaded, so all variants are tried and the first shipped
  // code wins. Convertible-only 2LT codes (HTM/HUK/HUL/HTN/HTP/HU6/HU7/HTQ) weren't
  // reachable from the given configurator URL without re-selecting Interior under
  // Convertible+2LT specifically, so those ids fall back to the 3LZ/coupe code for
  // the same color (already covered) rather than going unset.
  for (const variantId of Object.keys(_interiorColorRPO)) {
    const codeMap = _interiorColorRPO[variantId];
    for (const colorId of Object.keys(codeMap)) {
      const code = codeMap[colorId];
      if (uInt[colorId] && RPO_CODE_HAS_IMAGE.has(code)) {
        uInt[colorId].image = `${R}/rpo/${code}.jpg`;
      }
    }
  }

  // ── Option pricing ────────────────────────────────────────────────────────
  // Stamp the per-region `price` map onto every entry whose RPO code appears in
  // the AOL price schedule (see `_optionPriceUSD`). Done here rather than inline
  // so the schedule stays one auditable table: refreshing prices is a table swap,
  // not 200 hand edits. Entries with no RPO, or an RPO the schedule doesn't
  // list, keep their declared `price` (0) and render as standard equipment.
  // Accessories and exterior colours carry their RPO in `autoIntelRPOCode`, and
  // graphics-metadata says which trims each is offered on.
  for (const [bag, gBag] of [[uAcc, gAcc], [uExt, gExt]] as Array<[Record<string, any>, Record<string, any>]>) {
    for (const id of Object.keys(bag ?? {})) {
      const usd = _usdFor(bag[id]?.autoIntelRPOCode, gBag?.[id]?.relevantVariants ?? _PRICED_VARIANTS);
      if (usd != null) bag[id].price = _usdPrices(usd);
    }
  }
  // The default GT2 seat (seat_intJetBlack_gt2) is also scoped to coupe_2lt, whose
  // default interior is Jet Black Napa (see _NAPA_DEFAULT_INT in graphics-metadata).
  // AH2 (GT2 seats) is priced on 2LT only, so that scope would price this one seat
  // accessory at $1491.60 on EVERY trim - a price map is per accessory, not per
  // trim - billing 3LT/3LZ for standard seats and lifting the 2LT starting price.
  // Keep it at the no-cost price it already had (picking Jet Black Napa on 2LT was
  // never charged for the seat), so Stingray 2LT starts at its bare AED 386,200.
  if (uAcc?.seat_intJetBlack_gt2) uAcc.seat_intJetBlack_gt2.price = { default: 0 };
  // Interior colours carry no `autoIntelRPOCode` of their own — their RPO is
  // per-variant via _interiorColorRPO, which doubles as the "offered on" set.
  // The convertible maps are the coupe codes under `<id>Conv` keys, so a colour
  // resolves to the same price either way.
  const intVariants: Record<string, string[]> = {};
  for (const variantId of Object.keys(_interiorColorRPO)) {
    for (const colorId of Object.keys(_interiorColorRPO[variantId])) {
      (intVariants[colorId] ??= []).push(variantId);
    }
  }
  for (const colorId of Object.keys(intVariants)) {
    const variantId = intVariants[colorId][0];
    const usd = _usdFor(_interiorColorRPO[variantId][colorId], intVariants[colorId]);
    if (usd != null && uInt[colorId]) uInt[colorId].price = _usdPrices(usd);
  }
})();
